import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  computed,
  effect,
  inject,
  linkedSignal,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import {
  allResources,
  AppState,
  cancelParticipationEventById,
  ConfigService,
  createEvent,
  currentUser,
  DataService,
  deleteEventById,
  eventPatched,
  formatDurationMinutesForDisplay,
  finishEventById,
  getEventFinishRequests,
  getDefaultReservationSubject,
  getOnlineMeetingLink,
  getPatchedAvailabilityWithEvent,
  getTotallAvailability,
  IdentifiedAssistEvent,
  isValidEmail,
  loadEventsPatched,
  loadFavourites,
  OrdersService,
  refreshDashboard,
  RepetitiveEventsService,
  ResourceAttendee,
  ServiceSelectDataItem,
} from '@assist/shared/data';
import { AttendeeDropdownItem } from '@assist/ui/attendees-dropdown';
import { QuillLoadDirective } from '@assist/ui/structural';
import {
  FinishReservationData,
  ReservationDeleteComponent,
  ReservationFinishComponent,
} from '@assist/ui/reservation-popups';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import { DropdownlistComponent } from '@cue/assist/ui/dropdowns';
import { CheckboxComponent, TextboxComponent } from '@cue/assist/ui/inputs';
import { DateTimeSeparateInfoComponent } from '@cue/assist/ui/datetime-info';
import {
  ApiService,
  AssistResourceBM,
  eventModified,
  fromLocalized,
  getResourceIsAllowedMultipleResourceType,
  OrderDescriptor,
  OrderItemDescriptor,
  profileSettings,
  WebexUserSettingsModel,
} from '@cue/api';
import {
  CalendarAttendee,
  CalendarAvailability,
  CalendarEventDatetime,
  CalendarEventEditScope,
  CalendarEventRecurrence,
  CalendarEventReminder,
  CalendarsService,
  CalendarRecurringMutationScope,
  expandRecurrenceOccurrences,
  RepetitiveAvailabilityResult,
  resolveValidationWindowEnd,
} from '@cue/calendars';
import { LoadingComponent } from '@cue/loading';
import { ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent } from '@cue/modal';
import { DateFormatPipe, DateTimeFormatPipe, TimeFormatPipe } from '@cue/pipes';
import { translation } from '@cue/translations';
import {
  cueCheckCircle,
  cueClose,
  cueDelete,
  cueExclamationCircle,
  cueFinish,
  cuePencil,
  cueShuffle,
  IconComponent,
} from '@cue/ui/icons';
import { TooltipDirective } from '@cue/ui/tooltips';
import { QRService, RtlDirective } from '@cue/utilities';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { catchError, delay, forkJoin, map, of, switchMap, take, tap } from 'rxjs';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LicensingService } from '@cue/licensing';
import { remove } from 'remove-accents';
import { QuillEditorComponent } from 'ngx-quill';
import { add, addDays, addMinutes, differenceInMinutes, endOfDay, set, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  NewReservationDialogData,
  NewReservationDialogMode,
  NewReservationDialogResult,
  NewReservationDialogUiState,
  type NewReservationCreatedEventResult,
} from './new-reservation-dialog.data';
import {
  NewReservationAttendeeModel,
  NewReservationAttendeeParticipation,
  NewReservationAttendeeResponseStatus,
  NewReservationAttendeeRow,
  NewReservationLocationDetailOrderRow,
  NewReservationOrderDraft,
  NewReservationOrderItemDraft,
  NewReservationParticipationOption,
  NewReservationResourceAvailabilityDisplay,
  NewReservationResourcePickerItem,
  NewReservationResourceRow,
} from './new-reservation-dialog.models';
import { NewReservationAttendeeItemComponent } from './new-reservation-attendee-item/new-reservation-attendee-item.component';
import { NewReservationAttendeePickerComponent } from './new-reservation-attendee-picker/new-reservation-attendee-picker.component';
import { NewReservationLocationDetailAccordionItemComponent } from './new-reservation-location-detail-accordion-item/new-reservation-location-detail-accordion-item.component';
import { NewReservationLocationPickerComponent } from './new-reservation-location-picker/new-reservation-location-picker.component';
import { NewReservationLocationRowComponent } from './new-reservation-location-row/new-reservation-location-row.component';
import {
  isOccurrenceLikeEvent,
  resolveRecurringMutationScope,
  resolveRecurringMutationTargetEventId,
  resolveSingleOccurrenceMutation,
} from '../../helpers/recurring-event-mutation.helpers';
import { showAsMap } from '../reservation-detail/show-as.map';
import {
  replaceReservationPlaceholders,
  ReservationPlaceholderValues,
  reservationPlaceholderKeys,
} from '../../helpers/reservation-placeholders.helpers';
import { RecurrenceEditorComponent } from '../recurrence-editor/recurrence-editor.component';
import { formatSchedulingRulesForDisplay } from '../../helpers/reservation-scheduling-rules.helpers';
import {
  hasErrorAvailabilityDisableReason,
  ReservationAvailabilityDisableReason,
} from '../../helpers/reservation-availability-reasons.helpers';

type NewReservationOnlineMeetingProvider = 'teamsForBusiness' | 'hangoutsMeet' | 'webex';

type NewReservationOnlineMeetingOption = {
  text: string;
  value: NewReservationOnlineMeetingProvider | null;
};

type NewReservationSaveDisableReason = ReservationAvailabilityDisableReason;

type NewReservationResourcePickerOrigin = 'location' | 'emptyDetails';

type ExistingReservationLoadParams = {
  eventId: string;
  occurrenceId?: string;
  ownerEmail: string;
  resources: AssistResourceBM[];
};

type NewReservationShowAsOption = {
  key: string;
  text: string;
  value: string;
};

type NewReservationAttendeeAvailabilityLoadParams = {
  key: string;
  emails: string[];
  start: Date;
  end: Date;
};

type NewReservationAllowedOrderServicesLoadParams = {
  key: string;
  resources: AssistResourceBM[];
};

type NewReservationOnlineMeetingForSave = {
  onlineMeetingProvider: string;
} | null;

type NewReservationLocationForSave = {
  roomName: string;
  roomEmail: string;
  roomUsername?: string;
};

type NewReservationAttendeeForSave = {
  isResource: boolean;
  address: string;
  name: string;
  type: NewReservationAttendeeParticipation;
};

type NewReservationSavePayload = {
  visibility: 'public' | 'private';
  showAs: NewReservationShowAsOption;
  subject: string;
  bodyHtml: string;
  start: Date;
  end: Date;
  isAllDay: boolean;
  resourceAttendees: ResourceAttendee[];
  locations: NewReservationLocationForSave[];
  nonResourceAttendees: NewReservationAttendeeForSave[];
  calendarAttendees: NewReservationAttendeeForSave[];
  onlineMeeting: NewReservationOnlineMeetingForSave;
  webexMeeting: WebexUserSettingsModel | null;
  reminder: CalendarEventReminder;
  orders: OrderDescriptor[];
  totalOrderItemsCount: number;
};

type NewReservationFormValue = {
  subject: string;
  bodyHtml: string;
  showAs: NewReservationShowAsOption;
  allDay: boolean;
  start: Date;
  end: Date;
  recurrence: CalendarEventRecurrence | null;
  onlineMeetingProvider: string | null;
  editWholeSeries: boolean;
};

type NewReservationFormSeed = {
  key: string;
  value: NewReservationFormValue;
};

type NewReservationDateRange =
  | {
      kind: 'timed';
      start: Date;
      end: Date;
    }
  | {
      kind: 'allDay';
      startDate: string;
      endDate: string;
    };

type NewReservationAvailabilityRange = {
  start: Date;
  end: Date;
};

type NewReservationResourceSeed = {
  key: string;
  resourceIds: number[];
};

type NewReservationResourceAvailabilityLoadParams = {
  key: string;
  resources: AssistResourceBM[];
  start: Date;
  end: Date;
};

type NewReservationSelectedResourceAvailabilityLoadParams = NewReservationResourceAvailabilityLoadParams & {
  emails: string[];
  iCalUid: string | null;
  currentEventId: string | null;
};

type NewReservationRepetitiveAvailabilityLoadParams = {
  key: string;
  start: Date;
  end: Date;
  recurrence: CalendarEventRecurrence;
  resourceEmails: string[];
};

type NewReservationRecurringSchedulingValidationResult = {
  effectiveRecurrence: CalendarEventRecurrence;
  blockingReasons: string[];
  warningReasons: string[];
};

type NewReservationAttendeeSeed = {
  key: string;
  attendees: NewReservationAttendeeModel[];
};

enum RecurringOverLimitActionId {
  Disallow = 1,
  ScopeOut = 2,
  IgnoreAndWarn = 3,
}

function equalDateRange(previous: NewReservationAvailabilityRange, current: NewReservationAvailabilityRange): boolean {
  return previous.start.getTime() === current.start.getTime() && previous.end.getTime() === current.end.getTime();
}

function equalLoadParamsByKey<T extends { key: string }>(previous: T | undefined, current: T | undefined): boolean {
  return previous?.key === current?.key;
}

@Component({
  selector: 'assist-new-reservation-dialog',
  templateUrl: './new-reservation-dialog.component.html',
  styleUrls: ['./new-reservation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DateFormatPipe,
    DateTimeFormatPipe,
    DateTimeSeparateInfoComponent,
    DropdownlistComponent,
    IconComponent,
    LoadingComponent,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    ModalHeaderComponent,
    NewReservationAttendeeItemComponent,
    NewReservationAttendeePickerComponent,
    NewReservationLocationDetailAccordionItemComponent,
    NewReservationLocationPickerComponent,
    NewReservationLocationRowComponent,
    QuillEditorComponent,
    QuillLoadDirective,
    ReactiveFormsModule,
    RecurrenceEditorComponent,
    RtlDirective,
    TextboxComponent,
    TooltipDirective,
    TranslocoPipe,
  ],
})
export class NewReservationDialogComponent {
  readonly modal = inject<DialogRef<NewReservationDialogResult>>(DialogRef, { optional: true });
  readonly dialogData = inject<NewReservationDialogData | null>(DIALOG_DATA, { optional: true });
  private readonly apiService = inject(ApiService);
  private readonly calendarService = inject(CalendarsService);
  readonly configService = inject(ConfigService);
  private readonly dataService = inject(DataService);
  private readonly dateFormatPipe = inject(DateFormatPipe);
  private readonly dialog = inject(Dialog);
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly licensingService = inject(LicensingService);
  private readonly ordersService = inject(OrdersService);
  private readonly qrService = inject(QRService);
  private readonly repetitiveEventsService = inject(RepetitiveEventsService);
  private readonly store = inject<Store<AppState>>(Store<AppState>);
  private readonly timeFormatPipe = inject(TimeFormatPipe);
  private readonly translocoService = inject(TranslocoService);
  private appliedFormSeedKey: string | null = null;

  readonly close = output<NewReservationDialogResult>();
  readonly translation = translation;
  readonly icons = {
    cueCheckCircle,
    cueClose,
    cueDelete,
    cueExclamationCircle,
    cueFinish,
    cuePencil,
    cueShuffle,
  };
  readonly placeholderList = reservationPlaceholderKeys.map((placeholder) => `{${placeholder}}`).join(' ');
  readonly descriptionEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ header: 1 }, { header: 2 }, { header: 3 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
  };
  readonly descriptionEditorStyles = { height: '200px', width: '100%' };
  private readonly resourcePickerPageSize = 20;
  private nextOrderDraftItemId = 0;
  private readonly locationDetailResourceAttendeesCache = new Map<
    number,
    { resourceAttendee: ResourceAttendee; resourceAttendees: ResourceAttendee[] }
  >();

  readonly saving = signal(false);
  readonly saveFailed = signal(false);
  readonly mode = linkedSignal((): NewReservationDialogMode => this.dialogData?.mode ?? 'new');
  private readonly allResources = toSignal(this.store.select(allResources));
  private readonly currentUser = toSignal(this.store.select(currentUser));
  private readonly existingDialogData = computed(() => {
    return this.dialogData?.mode === 'detail' || this.dialogData?.mode === 'edit' ? this.dialogData : null;
  });
  private readonly resourcesForLoading = computed(() => {
    const resourcesPack = this.allResources();

    if (!resourcesPack || resourcesPack.loading) {
      return undefined;
    }

    if (resourcesPack.error || !resourcesPack.data) {
      return null;
    }

    const resourceIds = this.existingDialogData()?.resourceIds ?? [];

    if (resourceIds.length === 0) {
      return resourcesPack.data;
    }

    const resourceIdSet = new Set(resourceIds);
    const resources = resourcesPack.data.filter((resource) => resourceIdSet.has(resource.idForFiltering));

    return resources.length === resourceIds.length ? resources : null;
  });
  private readonly detailLoadParams = computed((): ExistingReservationLoadParams | undefined => {
    const dialogData = this.existingDialogData();
    const resources = this.resourcesForLoading();

    if (!dialogData || !dialogData.eventId || !dialogData.ownerEmail || resources == null) {
      return undefined;
    }

    return {
      eventId: dialogData.eventId,
      occurrenceId: dialogData.occurrenceId,
      ownerEmail: dialogData.ownerEmail,
      resources,
    };
  });

  readonly loadedReservation = rxResource({
    params: () => this.detailLoadParams(),
    stream: ({ params }) =>
      this.dataService.getEventById(params.ownerEmail, params.eventId, params.resources, params.occurrenceId).pipe(
        map((reservation) => {
          if (!reservation) {
            throw new Error('Reservation detail could not be loaded.');
          }

          return reservation;
        })
      ),
  });
  readonly reservation = computed<IdentifiedAssistEvent | null>(() => {
    return this.mode() === 'new' ? null : (this.loadedReservation.value() ?? null);
  });
  readonly showAsOptions = computed<NewReservationShowAsOption[]>(() =>
    (showAsMap[this.configService.getMode()] ?? []).map((option) => {
      const key = this.getShowAsLabelKey(option.value);

      return {
        ...option,
        key,
        text: this.translocoService.translate(key),
      };
    })
  );
  readonly reservationForm = this.formBuilder.group({
    subject: this.formBuilder.control('', { validators: [Validators.required] }),
    bodyHtml: this.formBuilder.control(''),
    showAs: this.formBuilder.control<NewReservationShowAsOption>(this.getDefaultShowAs()),
    allDay: this.formBuilder.control(false),
    start: this.formBuilder.control(this.getDefaultStart()),
    end: this.formBuilder.control(this.getDefaultEnd(this.getDefaultStart())),
    recurrence: this.formBuilder.control<CalendarEventRecurrence | null>(null),
    onlineMeetingProvider: this.formBuilder.control<string | null>(null),
    editWholeSeries: this.formBuilder.control(false),
  });
  readonly formRawValue = toSignal(this.reservationForm.valueChanges, {
    initialValue: this.reservationForm.getRawValue(),
  });
  readonly supportsAllDayEvents = computed(() => this.calendarService.supportsAllDayEvents());
  readonly formUsesAllDayDateOnly = computed(() => this.supportsAllDayEvents() && this.formRawValue().allDay);
  private readonly formStart = toSignal(this.reservationForm.controls.start.valueChanges, {
    initialValue: this.reservationForm.controls.start.value,
  });
  private readonly formEnd = toSignal(this.reservationForm.controls.end.valueChanges, {
    initialValue: this.reservationForm.controls.end.value,
  });
  private readonly formRecurrence = toSignal(this.reservationForm.controls.recurrence.valueChanges, {
    initialValue: this.reservationForm.controls.recurrence.value,
  });
  private readonly formEditWholeSeries = toSignal(this.reservationForm.controls.editWholeSeries.valueChanges, {
    initialValue: this.reservationForm.controls.editWholeSeries.value,
  });
  private readonly availabilityRange = computed<NewReservationAvailabilityRange>(
    () => ({
      start: this.normalizeDateInput(this.formStart()),
      end: this.normalizeDateInput(this.formEnd()),
    }),
    { equal: equalDateRange }
  );
  readonly locationDetailEventStart = computed(() => this.formStart());
  private readonly formSeed = computed<NewReservationFormSeed>(() => {
    const reservation = this.reservation();

    if (this.mode() !== 'new' && reservation) {
      return {
        key: `reservation:${reservation.id}:${reservation.occurrenceId ?? ''}:${reservation.type}`,
        value: {
          subject: reservation.subject,
          bodyHtml: reservation.bodyHtml,
          showAs: this.resolveShowAsOption(reservation.showAs) ?? this.getDefaultShowAs(),
          allDay: this.supportsAllDayEvents() && reservation.isAllDay === true,
          start: new Date(reservation.start.utcDateTime),
          end: new Date(reservation.end.utcDateTime),
          recurrence: reservation.recurrence ?? null,
          onlineMeetingProvider: this.resolveExistingOnlineMeetingProvider(reservation),
          editWholeSeries: false,
        },
      };
    }

    const createData = this.dialogData?.mode === 'new' ? this.dialogData : null;
    const start = createData?.initialStart ?? this.getDefaultStart();

    return {
      key: 'new',
      value: {
        subject: createData?.initialSubject ?? getDefaultReservationSubject(this.configService, this.translocoService),
        bodyHtml: createData?.initialBodyHtml ?? '',
        showAs: this.getDefaultShowAs(),
        allDay: this.supportsAllDayEvents() && createData?.initialIsAllDay === true,
        start,
        end: createData?.initialEnd ?? this.getDefaultEnd(start),
        recurrence: null,
        onlineMeetingProvider: this.getDefaultOnlineMeetingProvider(),
        editWholeSeries: false,
      },
    };
  });
  private readonly resourceSeed = computed<NewReservationResourceSeed>(() => {
    const reservation = this.reservation();

    if (this.mode() !== 'new' && reservation) {
      return {
        key: `reservation:${reservation.id}:${reservation.occurrenceId ?? ''}:${reservation.type}`,
        resourceIds: reservation.resourceAttendees.map((resourceAttendee) => resourceAttendee.resource.idForFiltering),
      };
    }

    const createData = this.dialogData?.mode === 'new' ? this.dialogData : null;

    return {
      key: 'new',
      resourceIds: createData?.initialResourceIds ?? [],
    };
  });
  readonly selectedResourceIds = linkedSignal<NewReservationResourceSeed, number[]>({
    source: () => this.resourceSeed(),
    computation: (seed, previous) => (previous?.source.key === seed.key ? previous.value : seed.resourceIds),
  });
  readonly selectedResources = computed(() => {
    const resourcesPack = this.allResources();
    const reservationResources =
      this.reservation()?.resourceAttendees.map((resourceAttendee) => resourceAttendee.resource) ?? [];
    const resources = [...(resourcesPack?.data ?? []), ...reservationResources];
    const resourceById = new Map(resources.map((resource) => [resource.idForFiltering, resource]));

    return this.selectedResourceIds()
      .map((resourceId) => resourceById.get(resourceId))
      .filter((resource): resource is AssistResourceBM => !!resource);
  });
  readonly resourcePickerActive = signal(false);
  readonly resourcePickerOrigin = signal<NewReservationResourcePickerOrigin>('location');
  readonly resourcePickerReplaceResourceId = signal<number | null>(null);
  readonly resourcePickerSearchInput = signal('');
  readonly resourcePickerSearchTerm = signal('');
  readonly resourcePickerVisibleLimit = signal(this.resourcePickerPageSize);
  readonly resourcesLoading = computed(() => this.allResources()?.loading === true);
  readonly resourcePickerMatchedResources = computed(() => {
    const resourcesPack = this.allResources();
    const selectedResourceIds = new Set(this.selectedResourceIds());
    const query = this.normalizeSearchText(this.resourcePickerSearchTerm());
    const resources = resourcesPack?.data ?? [];

    return resources
      .filter((resource) => !selectedResourceIds.has(resource.idForFiltering))
      .filter((resource) => this.isResourceVisibleForReservation(resource))
      .filter((resource) => query.length === 0 || this.getResourceSearchText(resource).includes(query))
      .sort((a, b) => this.getResourceDisplayName(a).localeCompare(this.getResourceDisplayName(b)));
  });
  readonly resourcePickerVisibleResources = computed(() =>
    this.resourcePickerMatchedResources().slice(0, this.resourcePickerVisibleLimit())
  );
  readonly resourcePickerRemainingCount = computed(() =>
    Math.max(0, this.resourcePickerMatchedResources().length - this.resourcePickerVisibleResources().length)
  );
  private readonly resourcePickerAvailabilityParams = computed<
    NewReservationResourceAvailabilityLoadParams | undefined
  >(
    () => {
      if (!this.resourcePickerActive()) {
        return undefined;
      }

      const resources = this.resourcePickerVisibleResources();
      const { start, end } = this.availabilityRange();

      if (resources.length === 0 || end <= start) {
        return undefined;
      }

      return {
        key: `${resources.map((resource) => resource.idForFiltering).join(',')}:${start.getTime()}:${end.getTime()}`,
        resources,
        start,
        end,
      };
    },
    { equal: equalLoadParamsByKey }
  );
  private readonly resourcePickerAvailability = rxResource({
    params: () => this.resourcePickerAvailabilityParams(),
    stream: ({ params }) => this.loadResourceAvailabilities(params.resources, params.start, params.end, false),
  });
  private readonly resourcePickerAvailabilityById = computed(
    () => this.resourcePickerAvailability.value() ?? new Map()
  );
  readonly resourcePickerItems = computed<NewReservationResourcePickerItem[]>(() => {
    const availabilityById = this.resourcePickerAvailabilityById();
    const availabilityParams = this.resourcePickerAvailabilityParams();

    return this.resourcePickerVisibleResources().map((resource) => {
      const availability =
        availabilityById.get(resource.idForFiltering) ??
        (availabilityParams ? this.getLoadingAvailabilityDisplay() : this.getUnknownAvailabilityDisplay());

      return {
        id: resource.idForFiltering,
        resource,
        displayName: this.getResourceDisplayName(resource),
        detail: this.getResourcePickerDetail(resource),
        schedulingRules: this.getSchedulingRulesForDisplay(resource),
        ...availability,
      };
    });
  });
  private readonly selectedResourceAvailabilityParams = computed<
    NewReservationSelectedResourceAvailabilityLoadParams | undefined
  >(
    () => {
      const resources = this.selectedResources();
      const { start, end } = this.availabilityRange();

      if (resources.length === 0 || end <= start) {
        return undefined;
      }

      const emails = Array.from(
        new Set(resources.map((resource) => this.normalizeEmail(resource.email)).filter(Boolean))
      ).sort();
      const reservation = this.reservation();
      const iCalUid = reservation?.iCalUId ?? null;
      const currentEventId = reservation?.id ?? this.existingDialogData()?.eventId ?? null;

      return {
        key: [
          resources.map((resource) => resource.idForFiltering).join(','),
          start.getTime(),
          end.getTime(),
          iCalUid ?? '',
          currentEventId ?? '',
        ].join(':'),
        resources,
        emails,
        start,
        end,
        iCalUid,
        currentEventId,
      };
    },
    { equal: equalLoadParamsByKey }
  );
  private readonly selectedResourceAvailability = rxResource({
    params: () => this.selectedResourceAvailabilityParams(),
    stream: ({ params }) =>
      forkJoin({
        availabilityById: this.loadResourceAvailabilities(params.resources, params.start, params.end, true),
        restrictedResourceIds: this.apiService
          .call(
            getResourceIsAllowedMultipleResourceType({
              emails: params.emails,
              start: params.start,
              end: params.end,
              iCalUid: params.iCalUid,
              currentEventId: params.currentEventId,
            })
          )
          .pipe(
            map(
              (response) =>
                new Set(
                  (response.resources ?? [])
                    .filter((resource) => resource.notAllowedMultipleResourceReservationsForResourceType)
                    .map((resource) => resource.resourceId)
                )
            ),
            catchError(() => of(null))
          ),
      }),
  });
  private readonly selectedResourceAvailabilityById = computed(
    () => this.selectedResourceAvailability.value()?.availabilityById ?? new Map()
  );
  private readonly restrictedSelectedResourceIds = computed(
    () => this.selectedResourceAvailability.value()?.restrictedResourceIds ?? new Set<number>()
  );
  private readonly resourceTypeRestrictionCheckFailed = computed(
    () => this.selectedResourceAvailability.value()?.restrictedResourceIds === null
  );
  private readonly webexSettings = rxResource({
    params: () => (this.configService.value.webexEnabled ? true : undefined),
    stream: () =>
      this.apiService.call(profileSettings()).pipe(
        map((response) => response.data ?? null),
        catchError(() => of(null))
      ),
  });
  private readonly pairedWebexSettings = computed(() => {
    const settings = this.webexSettings.value();

    return this.configService.value.webexEnabled && settings?.paired === true ? settings : null;
  });
  private readonly recurringSchedulingValidation = computed<NewReservationRecurringSchedulingValidationResult | null>(
    () => {
      const recurrence = this.resolveRecurrenceForSave();

      if (!this.shouldCheckRepetitiveAvailability(recurrence)) {
        return null;
      }

      return this.getRecurringSchedulingValidation(recurrence);
    }
  );
  private readonly repetitiveAvailabilityParams = computed<NewReservationRepetitiveAvailabilityLoadParams | undefined>(
    () => {
      const recurringSchedulingValidation = this.recurringSchedulingValidation();

      if (!recurringSchedulingValidation || recurringSchedulingValidation.blockingReasons.length > 0) {
        return undefined;
      }

      const resources = this.selectedResources();
      const { start, end } = this.availabilityRange();

      if (resources.length === 0 || end <= start) {
        return undefined;
      }

      const resourceEmails = resources.map((resource) => resource.email);

      return {
        key: JSON.stringify({
          start: start.getTime(),
          end: end.getTime(),
          recurrence: recurringSchedulingValidation.effectiveRecurrence,
          resourceEmails,
        }),
        start,
        end,
        recurrence: recurringSchedulingValidation.effectiveRecurrence,
        resourceEmails,
      };
    },
    { equal: equalLoadParamsByKey }
  );
  private readonly repetitiveAvailability = rxResource({
    params: () => this.repetitiveAvailabilityParams(),
    stream: ({ params }) =>
      this.calendarService
        .checkRepetitiveAvailability({
          start: CalendarEventDatetime.fromBrowserDatetime(params.start),
          end: CalendarEventDatetime.fromBrowserDatetime(params.end),
          recurrence: params.recurrence,
          resourceEmails: params.resourceEmails,
        })
        .pipe(
          map((result) => this.getRepetitiveAvailabilityDisableReasons(result)),
          catchError(() =>
            of([
              {
                level: 'error',
                message: this.translocoService.translate(
                  translation.newReservationUi.save.repetitiveAvailabilityCheckFailed
                ),
              } satisfies NewReservationSaveDisableReason,
            ])
          )
        ),
  });
  private readonly repetitiveAvailabilityDisableReasons = computed(() => this.repetitiveAvailability.value() ?? []);
  readonly resourceRows = computed<NewReservationResourceRow[]>(() =>
    this.selectedResources().map((resource) => ({
      resource,
      displayName: this.getResourceDisplayName(resource),
      ...this.getSelectedResourceAvailabilityState(resource),
      schedulingRules: this.getSchedulingRulesForDisplay(resource),
      canRemove: this.canRemoveResource(resource),
    }))
  );
  private readonly attendeeSeed = computed<NewReservationAttendeeSeed>(() => {
    const reservation = this.reservation();

    if (this.mode() !== 'new' && reservation) {
      return {
        key: `reservation:${reservation.id}:${reservation.occurrenceId ?? ''}:${reservation.type}`,
        attendees: this.createAttendeeModelsFromReservation(reservation),
      };
    }

    const createData = this.dialogData?.mode === 'new' ? this.dialogData : null;
    const seededAttendees = this.createAttendeeModelsFromEmails(
      createData?.initialRequiredAttendeeEmails ?? [],
      createData?.initialOptionalAttendeeEmails ?? []
    );
    const user = this.currentUser()?.data;
    const userEmail = this.normalizeEmail(user?.email ?? '');

    if (!userEmail) {
      return {
        key: 'new',
        attendees: seededAttendees,
      };
    }

    return {
      key: `new:${userEmail}`,
      attendees: [
        {
          email: user?.email ?? userEmail,
          name: user?.name || userEmail,
          participation: 'required',
          isOrganizer: true,
          responseStatus: 'none',
        },
        ...seededAttendees.filter((attendee) => this.normalizeEmail(attendee.email) !== userEmail),
      ],
    };
  });
  readonly selectedAttendees = linkedSignal<NewReservationAttendeeSeed, NewReservationAttendeeModel[]>({
    source: () => this.attendeeSeed(),
    computation: (seed, previous) => (previous?.source.key === seed.key ? previous.value : seed.attendees),
  });
  readonly attendeePickerValue = signal<AttendeeDropdownItem[]>([]);
  readonly attendeePickerActive = signal(false);
  readonly attendeePickerSearchTerm = signal('');
  readonly attendeePickerCustomEmail = computed(() => {
    const email = this.normalizeEmail(this.attendeePickerSearchTerm());

    if (!isValidEmail(email)) {
      return null;
    }

    const existingAttendeeEmails = new Set(
      this.selectedAttendees().map((attendee) => this.normalizeEmail(attendee.email))
    );

    return existingAttendeeEmails.has(email) ? null : email;
  });
  readonly participationOptions = computed<NewReservationParticipationOption[]>(() => [
    {
      text: this.translocoService.translate(translation.newReservationUi.administration.required),
      value: 'required',
    },
    {
      text: this.translocoService.translate(translation.newReservationUi.administration.optional),
      value: 'optional',
    },
  ]);
  private readonly attendeeAvailabilityEmailKey = computed(() =>
    this.selectedAttendees()
      .map((attendee) => this.normalizeEmail(attendee.email))
      .filter(Boolean)
      .sort()
      .join('|')
  );
  private readonly attendeeAvailabilityParams = computed<NewReservationAttendeeAvailabilityLoadParams | undefined>(
    () => {
      const emailKey = this.attendeeAvailabilityEmailKey();
      const { start, end } = this.availabilityRange();

      if (!emailKey || end <= start) {
        return undefined;
      }

      return {
        key: `${emailKey}:${start.getTime()}:${end.getTime()}`,
        emails: emailKey.split('|'),
        start,
        end,
      };
    },
    { equal: equalLoadParamsByKey }
  );
  private readonly attendeeAvailability = rxResource({
    params: () => this.attendeeAvailabilityParams(),
    stream: ({ params }) =>
      forkJoin(
        params.emails.map((email) =>
          this.resolveAttendeeAvailability(email, params.start, params.end).pipe(
            map((availability) => [email, availability] satisfies [string, NewReservationResourceAvailabilityDisplay])
          )
        )
      ).pipe(map((entries) => new Map<string, NewReservationResourceAvailabilityDisplay>(entries))),
  });
  private readonly attendeeAvailabilityByEmail = computed(() => this.attendeeAvailability.value() ?? new Map());
  readonly attendeeRows = computed<NewReservationAttendeeRow[]>(() =>
    this.selectedAttendees().map((attendee) => ({
      ...attendee,
      initials: this.getAttendeeInitials(attendee),
      participationLabel: this.getParticipationLabel(attendee.participation),
      responseStatusLabel: this.getAttendeeResponseStatusLabel(attendee.responseStatus),
      ...this.getAttendeeAvailabilityState(attendee),
    }))
  );
  readonly organizerAttendee = computed(() => this.attendeeRows().find((attendee) => attendee.isOrganizer) ?? null);
  readonly regularAttendees = computed(() => this.attendeeRows().filter((attendee) => !attendee.isOrganizer));
  readonly onlineMeetingOptions = computed<NewReservationOnlineMeetingOption[]>(() => {
    const options: NewReservationOnlineMeetingOption[] = [
      {
        text: this.translocoService.translate(translation.newReservationUi.location.onlineMeetingNone),
        value: null,
      },
    ];

    if (this.configService.isO365()) {
      options.push({
        text: this.translocoService.translate(translation.newReservationUi.location.onlineMeetingTeams),
        value: 'teamsForBusiness',
      });
    }

    if (this.configService.isGspace()) {
      options.push({
        text: this.translocoService.translate(translation.newReservationUi.location.onlineMeetingMeet),
        value: 'hangoutsMeet',
      });
    }

    if (this.pairedWebexSettings()) {
      options.push({
        text: this.translocoService.translate(translation.newReservationUi.location.onlineMeetingWebex),
        value: 'webex',
      });
    }

    return options;
  });
  readonly anyPremiumResourceLikeSelected = computed(() =>
    this.selectedResources().some((resource) => this.licensingService.isPremiumResourceLike(resource))
  );
  readonly showOnlineMeetingControls = computed(() => {
    return (
      this.anyPremiumResourceLikeSelected() &&
      (this.onlineMeetingOptions().length > 1 || !!this.reservation()?.onlineMeetingProvider)
    );
  });
  readonly onlineMeetingLink = computed(() => getOnlineMeetingLink(this.reservation(), this.document));
  private readonly document = inject(DOCUMENT);
  readonly orderDrafts = linkedSignal<number[], NewReservationOrderDraft[]>({
    source: () => this.selectedResourceIds(),
    computation: (resourceIds, previous) => {
      const previousDrafts = new Map(
        (previous?.value ?? []).map(
          (draft) => [draft.resourceIdForFiltering, draft] satisfies [number, NewReservationOrderDraft]
        )
      );

      return resourceIds.map(
        (resourceIdForFiltering) =>
          previousDrafts.get(resourceIdForFiltering) ?? {
            resourceIdForFiltering,
            note: '',
            items: [],
          }
      );
    },
  });
  readonly expandedLocationDetailResourceIds = signal<Record<number, boolean>>({});
  readonly addingOrderItemResourceIds = signal<Record<number, boolean>>({});
  private readonly previousOrdersParams = computed(() => {
    const reservation = this.reservation();

    if (!this.configService.value.enableOrders || !reservation) {
      return undefined;
    }

    return {
      key: `${reservation.id}:${reservation.iCalUId}`,
      id: reservation.id,
      iCalUid: reservation.iCalUId,
    };
  });
  readonly previousOrders = rxResource({
    params: () => this.previousOrdersParams(),
    stream: ({ params }) =>
      this.ordersService.getEventOrders({ id: params.id, iCalUid: params.iCalUid }).pipe(catchError(() => of([]))),
  });
  private readonly previousOrdersByResourceId = computed(() => {
    const previousOrders = this.previousOrders.value() ?? [];
    const byResourceId = new Map<number, OrderDescriptor[]>();

    for (const order of previousOrders) {
      if (order.resourceIdForFiltering == null) {
        continue;
      }

      byResourceId.set(order.resourceIdForFiltering, [
        ...(byResourceId.get(order.resourceIdForFiltering) ?? []),
        order,
      ]);
    }

    return byResourceId;
  });
  private readonly allowedOrderServicesParams = computed<NewReservationAllowedOrderServicesLoadParams | undefined>(
    () => {
      if (!this.configService.value.enableOrders) {
        return undefined;
      }

      const resources = this.selectedResources();

      if (resources.length === 0) {
        return undefined;
      }

      return {
        key: resources.map((resource) => resource.idForFiltering).join(','),
        resources,
      };
    }
  );
  private readonly allowedOrderServicesByResource = rxResource({
    params: () => this.allowedOrderServicesParams(),
    stream: ({ params }) =>
      forkJoin(
        params.resources.map((resource) =>
          this.ordersService.getAllowedServiceIdsForResourceGuid(resource.id).pipe(
            catchError(() => of([])),
            map((serviceIds) => [resource.idForFiltering, serviceIds] satisfies [number, number[]])
          )
        )
      ).pipe(map((entries) => new Map<number, number[]>(entries))),
  });
  private readonly allowedOrderServicesByResourceId = computed(
    () => this.allowedOrderServicesByResource.value() ?? new Map<number, number[]>()
  );
  private readonly selectedResourceAttendees = computed(() => {
    const reservationResourceAttendees = this.reservation()?.resourceAttendees ?? [];

    return this.selectedResources().map((resource) => {
      return (
        reservationResourceAttendees.find(
          (resourceAttendee) => resourceAttendee.resource.idForFiltering === resource.idForFiltering
        ) ?? this.createResourceAttendeeFromResource(resource)
      );
    });
  });
  readonly locationDetailOrderRows = computed<NewReservationLocationDetailOrderRow[]>(() => {
    const orderDrafts = new Map(
      this.orderDrafts().map(
        (draft) => [draft.resourceIdForFiltering, draft] satisfies [number, NewReservationOrderDraft]
      )
    );
    const previousOrdersByResourceId = this.previousOrdersByResourceId();
    const allowedOrderServicesByResourceId = this.allowedOrderServicesByResourceId();
    const orderServicesLoading = this.allowedOrderServicesByResource.isLoading();
    const addingOrderItemResourceIds = this.addingOrderItemResourceIds();
    const expandedLocationDetailResourceIds = this.expandedLocationDetailResourceIds();

    return this.selectedResourceAttendees().map((resourceAttendee) => {
      const resource = resourceAttendee.resource;
      const draft =
        orderDrafts.get(resource.idForFiltering) ??
        ({
          resourceIdForFiltering: resource.idForFiltering,
          note: '',
          items: [],
        } satisfies NewReservationOrderDraft);
      const previousOrders = previousOrdersByResourceId.get(resource.idForFiltering) ?? [];
      const allowedServiceIds = allowedOrderServicesByResourceId.get(resource.idForFiltering) ?? [];
      const hasOrders = draft.items.length > 0 || previousOrders.length > 0;

      return {
        resource,
        resourceAttendee,
        resourceAttendees: this.getStableLocationDetailResourceAttendees(resourceAttendee),
        displayName: this.getResourceDisplayName(resource),
        resourceTypeName: this.getResourceTypeName(resource),
        expanded: expandedLocationDetailResourceIds[resource.idForFiltering] ?? hasOrders,
        draft,
        previousOrders,
        orderServicesLoading,
        canAddOrders: this.configService.value.enableOrders && allowedServiceIds.length > 0,
        addingOrderItem: addingOrderItemResourceIds[resource.idForFiltering] === true,
      };
    });
  });
  readonly ordersValid = computed(() =>
    this.orderDrafts().every((draft) => draft.items.every((item) => !this.isOrderItemNoteInvalid(item)))
  );
  readonly saveAvailabilityLoading = computed(
    () => this.selectedResourceAvailability.isLoading() || this.repetitiveAvailability.isLoading()
  );
  readonly saveDisableReasons = computed<NewReservationSaveDisableReason[]>(() => {
    if (this.uiState() !== 'edit') {
      return [];
    }

    const reasons: NewReservationSaveDisableReason[] = [];
    const value = this.formRawValue();
    const start = this.normalizeDateInput(value.start);
    const end = this.normalizeDateInput(value.end);
    const durationInMinutes = Math.floor((end.getTime() - start.getTime()) / 60000);

    if (this.selectedResourceIds().length === 0) {
      reasons.push({
        level: 'error',
        message: this.translocoService.translate(translation.newReservationUi.save.disableNoResourceSelected),
      });
    }

    if (end <= start) {
      reasons.push({
        level: 'error',
        message: this.translocoService.translate(translation.newReservationUi.save.invalidTimeRange),
      });
    }

    if (end <= new Date()) {
      reasons.push({
        level: 'error',
        message: this.translocoService.translate(translation.newReservationUi.save.disablePast),
      });
    }

    for (const resource of this.selectedResources()) {
      const resourceName = this.getResourceDisplayName(resource);
      const minimumDuration = resource.schedulingSettings?.minimumMeetingDurationInMinutes;
      const maximumDuration = resource.schedulingSettings?.maximumMeetingDurationInMinutes;

      if (minimumDuration != null && durationInMinutes < minimumDuration) {
        reasons.push({
          level: 'error',
          message: this.translocoService.translate(
            translation.newReservationUi.save.disableMinimumDurationForResource,
            {
              resourceName,
              duration: formatDurationMinutesForDisplay(minimumDuration, (key) => this.translocoService.translate(key)),
            }
          ),
        });
      }

      if (maximumDuration != null && durationInMinutes > maximumDuration) {
        reasons.push({
          level: 'error',
          message: this.translocoService.translate(
            translation.newReservationUi.save.disableMaximumDurationForResource,
            {
              resourceName,
              duration: formatDurationMinutesForDisplay(maximumDuration, (key) => this.translocoService.translate(key)),
            }
          ),
        });
      }
    }

    for (const row of this.resourceRows()) {
      if (row.availabilityState !== 'error' && row.availabilityState !== 'warning') {
        continue;
      }

      const fallbackMessage = this.translocoService.translate(
        translation.newReservationUi.save.disableAvailabilityForResource,
        {
          resourceName: row.displayName,
        }
      );

      for (const message of row.availabilityReasons.length > 0 ? row.availabilityReasons : [fallbackMessage]) {
        reasons.push({
          level: row.availabilityState === 'error' ? 'error' : 'warning',
          message,
        });
      }
    }

    const recurringSchedulingValidation = this.recurringSchedulingValidation();
    if (recurringSchedulingValidation) {
      for (const message of recurringSchedulingValidation.blockingReasons) {
        reasons.push({ level: 'error', message });
      }

      for (const message of recurringSchedulingValidation.warningReasons) {
        reasons.push({ level: 'warning', message });
      }
    }

    reasons.push(...this.repetitiveAvailabilityDisableReasons());

    return this.getUniqueSaveDisableReasons(reasons);
  });
  readonly hasErrorSaveDisableReason = computed(() => hasErrorAvailabilityDisableReason(this.saveDisableReasons()));
  readonly dateTimePickers = viewChildren(DateTimeSeparateInfoComponent);
  readonly canSave = computed(() => {
    const value = this.formRawValue();
    const start = this.normalizeDateInput(value.start);
    const end = this.normalizeDateInput(value.end);

    return (
      this.uiState() === 'edit' &&
      !this.saving() &&
      this.dateTimePickers().every((picker) => picker.valid()) &&
      value.subject.trim().length > 0 &&
      end > start &&
      this.selectedResourceIds().length > 0 &&
      this.ordersValid() &&
      !this.saveAvailabilityLoading() &&
      !this.hasErrorSaveDisableReason()
    );
  });
  readonly dateRange = computed<NewReservationDateRange>(() => {
    const value = this.formRawValue();

    if (this.supportsAllDayEvents() && value.allDay) {
      return {
        kind: 'allDay',
        startDate: this.toDateOnlyString(value.start),
        endDate: this.toDateOnlyString(value.end),
      };
    }

    return {
      kind: 'timed',
      start: value.start,
      end: value.end,
    };
  });
  readonly repetitiveEventsEnabled = computed(
    () =>
      this.repetitiveEventsService.isEnabled() &&
      this.currentUser()?.data?.features.assistCreatingReccuringEvents === true
  );
  readonly repetitiveEventOptions = computed(() =>
    this.repetitiveEventsService.getOptions({
      mode: this.mode() === 'new' ? 'new' : 'detail',
      eventType: this.reservation()?.type,
    })
  );
  readonly supportsSeriesScopeSwitch = computed(() => {
    return this.isOccurrenceLikeEvent() && this.repetitiveEventOptions().allowSeriesMasterEditFromOccurrence;
  });
  readonly canEditRecurrencePattern = computed(() => {
    if (!this.repetitiveEventsEnabled()) {
      return false;
    }

    if (this.mode() === 'new') {
      return this.repetitiveEventOptions().allowRepetitiveCreate;
    }

    if (!this.repetitiveEventOptions().allowRepetitiveEdit) {
      return false;
    }

    const type = this.reservation()?.type;

    return type === 'singleInstance' || type === 'seriesMaster';
  });
  readonly readonlyOther = computed(() => this.dialogData?.readonlyOther === true);
  readonly loadErrorMessage = computed(() => {
    if (!this.hasRequiredIdentifiers(this.mode())) {
      return translation.newReservationUi.state.missingIdentifiers;
    }

    if (this.resourcesForLoading() === null) {
      return translation.newReservationUi.state.resourcesUnavailable;
    }

    return translation.newReservationUi.state.loadFailed;
  });

  private readonly dialogStateSource = computed(() => {
    const mode = this.mode();
    const existingMode = mode !== 'new';
    const hasRequiredIdentifiers = this.hasRequiredIdentifiers(mode);
    const resourcesForLoading = this.resourcesForLoading();
    const reservationLoadError = this.loadedReservation.error();
    const reservationLoaded = this.loadedReservation.hasValue() && this.loadedReservation.value() != null;
    const reservationMissing = this.loadedReservation.hasValue() && this.loadedReservation.value() == null;
    const reservationLoading = existingMode && hasRequiredIdentifiers && !reservationLoaded && !reservationLoadError;
    const readonlyOther = this.readonlyOther();

    return {
      mode,
      hasRequiredIdentifiers,
      readonlyOther,
      isLoading: reservationLoading && resourcesForLoading !== null,
      hasLoadError:
        existingMode &&
        hasRequiredIdentifiers &&
        (resourcesForLoading === null || !!reservationLoadError || reservationMissing),
    };
  });

  readonly uiState = linkedSignal({
    source: () => this.dialogStateSource(),
    computation: ({
      mode,
      hasRequiredIdentifiers,
      readonlyOther,
      isLoading,
      hasLoadError,
    }): NewReservationDialogUiState => {
      if (!hasRequiredIdentifiers) {
        return 'error';
      }

      if (hasLoadError) {
        return 'error';
      }

      if (isLoading) {
        return 'loading';
      }

      if (readonlyOther || mode === 'detail') {
        return 'readonly';
      }

      if (mode === 'edit' && !this.canEditCurrentReservation()) {
        return 'readonly';
      }

      return 'edit';
    },
  });

  readonly title = computed(() => {
    if (this.uiState() === 'error') {
      return translation.newReservationUi.modalTitle.detail;
    }

    switch (this.mode()) {
      case 'new':
        return translation.newReservationUi.state.createTitle;
      case 'edit':
        return translation.newReservationUi.state.editTitle;
      case 'detail':
        return translation.newReservationUi.modalTitle.detail;
    }
  });

  readonly canSwitchToEdit = computed(() => {
    const reservation = this.reservation();

    return (
      this.mode() === 'detail' &&
      this.uiState() === 'readonly' &&
      !this.dialogData?.forceReadonly &&
      !this.readonlyOther() &&
      reservation?.showChangeButton === true &&
      (reservation.isOrganizer === true || this.canEditAsNotOrganizer())
    );
  });

  readonly canEditAsNotOrganizer = computed(() => {
    const reservation = this.reservation();

    if (!this.configService.isTouchoneCalendar() || this.mode() === 'new' || !reservation) {
      return false;
    }

    const canEdit =
      (reservation.ownerEventId != null &&
        reservation.visibility.toLowerCase() === 'private' &&
        reservation.eventPermissions?.canEditParentEventPrivate) ||
      (reservation.visibility.toLowerCase() === 'public' && reservation.eventPermissions?.canEditParentEventPublic) ||
      (reservation.ownerEventId == null &&
        reservation.visibility.toLowerCase() === 'public' &&
        reservation.eventPermissions?.canEditPublic) ||
      (reservation.visibility.toLowerCase() === 'private' && reservation.eventPermissions?.canEditPrivate);

    return !reservation.isOrganizer && !!canEdit;
  });

  readonly isOccurrenceLikeEvent = computed(() => {
    return isOccurrenceLikeEvent(this.reservation());
  });

  readonly selectedEditScope = computed<CalendarEventEditScope>(() => {
    const reservation = this.reservation();

    if (!reservation) {
      return 'singleOccurrence';
    }

    if (reservation.type === 'seriesMaster') {
      return 'seriesMaster';
    }

    if (reservation.type === 'occurrence' || reservation.type === 'exception') {
      return this.formEditWholeSeries() ? 'seriesMaster' : 'singleOccurrence';
    }

    return 'singleOccurrence';
  });

  readonly showOrganizerDeleteActions = computed(() => {
    const reservation = this.reservation();

    return !this.readonlyOther() && this.mode() !== 'new' && reservation?.showDeleteButton === true;
  });

  readonly showConfirmAction = computed(() => {
    const reservation = this.reservation();

    return this.mode() !== 'new' && reservation?.showConfirmButton === true;
  });

  readonly showFinishAction = computed(() => {
    const reservation = this.reservation();

    return this.mode() !== 'new' && reservation?.showFinishButton === true;
  });

  readonly showCancelParticipation = computed(() => {
    const reservation = this.reservation();

    return (
      !this.readonlyOther() &&
      !this.dialogData?.forceReadonly &&
      !this.canEditAsNotOrganizer() &&
      reservation?.cancelled !== true &&
      reservation?.meAttending === true &&
      reservation?.isOrganizer === false &&
      this.mode() !== 'new'
    );
  });

  readonly showNonOrganizerDeleteActions = computed(() => {
    const reservation = this.reservation();

    return (
      !this.readonlyOther() &&
      this.canEditAsNotOrganizer() &&
      reservation?.cancelled !== true &&
      reservation?.isOrganizer === false &&
      this.mode() !== 'new'
    );
  });

  readonly showDeleteActions = computed(() => {
    return this.showOrganizerDeleteActions() || this.showNonOrganizerDeleteActions();
  });

  private readonly applyFormSeed = effect(() => {
    const seed = this.formSeed();

    if (this.appliedFormSeedKey === seed.key) {
      return;
    }

    this.appliedFormSeedKey = seed.key;
    this.reservationForm.reset(seed.value);
  });

  private readonly syncFormAvailability = effect(() => {
    if (this.uiState() === 'edit' && !this.saving()) {
      this.reservationForm.enable({ emitEvent: false });
      return;
    }

    this.reservationForm.disable({ emitEvent: false });
  });

  private readonly syncResourcePickerSearch = effect((onCleanup) => {
    const searchInput = this.resourcePickerSearchInput();
    const timeout = setTimeout(() => this.resourcePickerSearchTerm.set(searchInput), 250);

    onCleanup(() => clearTimeout(timeout));
  });

  private readonly applyDefaultWebexMeeting = effect(() => {
    if (
      this.mode() !== 'new' ||
      !this.configService.value.autoMeetingSettings_Webex ||
      !this.pairedWebexSettings() ||
      this.reservationForm.controls.onlineMeetingProvider.dirty ||
      this.reservationForm.controls.onlineMeetingProvider.value
    ) {
      return;
    }

    this.reservationForm.controls.onlineMeetingProvider.setValue('webex');
  });

  private readonly syncAllDaySupport = effect(() => {
    if (this.supportsAllDayEvents() || !this.reservationForm.controls.allDay.value) {
      return;
    }

    this.reservationForm.controls.allDay.setValue(false);
  });

  closeDialog() {
    if (this.dialogData?.allowCompact) {
      this.compactDialog();
      return;
    }

    this.closeWithResult({ action: 'closed' });
  }

  compactDialog() {
    const value = this.formRawValue();
    this.closeWithResult({
      action: 'compact',
      start: this.normalizeDateInput(value.start),
      end: this.normalizeDateInput(value.end),
    });
  }

  switchToEdit() {
    if (!this.canSwitchToEdit()) {
      return;
    }

    this.mode.set('edit');
  }

  cancelParticipation() {
    const reservation = this.reservation();

    if (!reservation || !this.showCancelParticipation()) {
      return;
    }

    this.closeWithResult({
      action: 'cancelledParticipation',
      eventId: reservation.id,
      iCalUId: reservation.iCalUId,
    });
    this.store.dispatch(
      cancelParticipationEventById({
        eventToCancelParticipationId: reservation.id,
        icalId: reservation.iCalUId,
      })
    );
  }

  onConfirm() {
    this.qrService.trigger$.next(null);
  }

  onFinish() {
    const reservation = this.reservation();

    if (!reservation || !this.showFinishAction()) {
      return;
    }

    this.dialog
      .open<number, FinishReservationData>(ReservationFinishComponent, {
        data: {
          reservation,
        },
      })
      .closed.pipe(take(1))
      .subscribe((result) => {
        if (result !== 1) {
          return;
        }

        this.store.dispatch(
          finishEventById({
            eventToFinishId: reservation.id,
            finishedStatusRequests: getEventFinishRequests(reservation),
          })
        );

        this.closeWithResult({ action: 'closed' });
      });
  }

  save() {
    if (!this.canSave()) {
      this.reservationForm.markAllAsTouched();
      return;
    }

    this.saveFailed.set(false);

    if (this.mode() === 'new') {
      this.createReservation();
      return;
    }

    this.patchReservation();
  }

  onDelete() {
    const reservation = this.reservation();

    if (!reservation) {
      return;
    }

    this.confirmDelete(reservation.id, 'event', { scope: 'all' });
  }

  onDeleteOccurrence() {
    const reservation = this.reservation();
    const mutation = reservation
      ? resolveSingleOccurrenceMutation(reservation, (event) =>
          this.calendarService.getSingleOccurrenceMutationTargetEventId(event)
        )
      : null;

    if (!reservation || !mutation) {
      return;
    }

    this.confirmDelete(mutation.targetEventId, 'occurrence', mutation.mutationScope);
  }

  onDeleteSeries() {
    const reservation = this.reservation();

    if (!reservation) {
      return;
    }

    this.confirmDelete(reservation.seriesMasterId ?? reservation.id, 'series', { scope: 'all' });
  }

  onStartChanged(event: { oldValue: Date; newValue: Date }) {
    const current = this.formRawValue();
    const start = this.normalizeDateInput(event.newValue);
    const currentEnd = this.normalizeDateInput(current.end);
    const currentStart = this.normalizeDateInput(current.start);
    const end = start >= currentEnd ? addMinutes(start, differenceInMinutes(currentStart, currentEnd)) : currentEnd;
    const patch: Partial<NewReservationFormValue> = {};

    if (start.getTime() !== currentStart.getTime()) {
      patch.start = start;
    }

    if (end.getTime() !== currentEnd.getTime()) {
      patch.end = end;
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.reservationForm.patchValue(patch);
  }

  onEndChanged(event: { oldValue: Date; newValue: Date }) {
    const current = this.formRawValue();
    const currentEnd = this.normalizeDateInput(current.end);
    const end = this.normalizeDateInput(event.newValue);

    if (end.getTime() === currentEnd.getTime()) {
      return;
    }

    this.reservationForm.patchValue({ end });
  }

  openResourcePicker(replaceResourceId: number | null = null, origin: NewReservationResourcePickerOrigin = 'location') {
    this.resourcePickerOrigin.set(origin);
    this.resourcePickerReplaceResourceId.set(replaceResourceId);
    this.resourcePickerActive.set(true);
  }

  openAttendeePicker() {
    this.attendeePickerSearchTerm.set('');
    this.attendeePickerActive.set(true);
  }

  closeResourcePicker() {
    this.resourcePickerActive.set(false);
    this.resourcePickerOrigin.set('location');
    this.resourcePickerReplaceResourceId.set(null);
    this.resourcePickerSearchInput.set('');
    this.resourcePickerSearchTerm.set('');
    this.resourcePickerVisibleLimit.set(this.resourcePickerPageSize);
  }

  onResourcePickerFilterChange(value: string) {
    this.resourcePickerSearchInput.set(value ?? '');
    this.resourcePickerVisibleLimit.set(this.resourcePickerPageSize);
  }

  onResourcePickerScrollEnd() {
    if (this.resourcePickerRemainingCount() === 0) {
      return;
    }

    this.resourcePickerVisibleLimit.update((limit) => limit + this.resourcePickerPageSize);
  }

  selectResourcePickerResource(item: NewReservationResourcePickerItem) {
    const resourceId = item.resource.idForFiltering;
    const replaceResourceId = this.resourcePickerReplaceResourceId();

    if (replaceResourceId != null) {
      this.selectedResourceIds.update((resourceIds) => {
        if (resourceIds.includes(resourceId)) {
          return resourceIds.filter((selectedResourceId) => selectedResourceId !== replaceResourceId);
        }

        const replaceIndex = resourceIds.indexOf(replaceResourceId);

        if (replaceIndex === -1) {
          return [...resourceIds, resourceId];
        }

        const nextResourceIds = [...resourceIds];
        nextResourceIds[replaceIndex] = resourceId;

        return nextResourceIds;
      });
      this.expandedLocationDetailResourceIds.update((expandedResourceIds) => {
        const nextExpandedResourceIds = { ...expandedResourceIds };
        const wasExpanded = nextExpandedResourceIds[replaceResourceId] === true;

        delete nextExpandedResourceIds[replaceResourceId];

        if (wasExpanded) {
          nextExpandedResourceIds[resourceId] = true;
        }

        return nextExpandedResourceIds;
      });
      this.closeResourcePicker();
      return;
    }

    if (!this.selectedResourceIds().includes(resourceId)) {
      this.selectedResourceIds.update((resourceIds) => [...resourceIds, resourceId]);
    }

    this.closeResourcePicker();
  }

  clearDescription() {
    this.reservationForm.controls.bodyHtml.setValue('');
  }

  onAttendeePickerValueChange(value: AttendeeDropdownItem[] | AttendeeDropdownItem | null) {
    const items = Array.isArray(value) ? value : value ? [value] : [];
    const attendeesToAdd = items
      .filter((item) => !item.isGroup && !!item.value)
      .map(
        (item): NewReservationAttendeeModel => ({
          email: item.value,
          name: item.text || item.value,
          participation: 'required',
          isOrganizer: false,
          responseStatus: 'none',
        })
      );

    if (attendeesToAdd.length > 0) {
      this.selectedAttendees.update((attendees) => this.mergeAttendees([...attendees, ...attendeesToAdd]));
      this.attendeePickerActive.set(false);
      this.attendeePickerSearchTerm.set('');
    }

    this.attendeePickerValue.set([]);
  }

  onAttendeePickerFilterChange(value: string) {
    this.attendeePickerSearchTerm.set(value);
  }

  addAttendeeByEmail(email: string) {
    this.selectedAttendees.update((attendees) =>
      this.mergeAttendees([
        ...attendees,
        {
          email,
          name: email,
          participation: 'required',
          isOrganizer: false,
          responseStatus: 'none',
        },
      ])
    );
    this.attendeePickerActive.set(false);
    this.attendeePickerSearchTerm.set('');
    this.attendeePickerValue.set([]);
  }

  setAttendeeParticipation(
    email: string,
    participation: NewReservationAttendeeParticipation | NewReservationParticipationOption
  ) {
    const normalizedEmail = this.normalizeEmail(email);
    const nextParticipation = typeof participation === 'string' ? participation : participation.value;

    this.selectedAttendees.update((attendees) =>
      attendees.map((attendee) =>
        this.normalizeEmail(attendee.email) === normalizedEmail
          ? { ...attendee, participation: nextParticipation }
          : attendee
      )
    );
  }

  removeAttendee(email: string) {
    const normalizedEmail = this.normalizeEmail(email);

    this.selectedAttendees.update((attendees) =>
      attendees.filter((attendee) => this.normalizeEmail(attendee.email) !== normalizedEmail)
    );
  }

  toggleLocationDetail(resourceIdForFiltering: number) {
    const currentlyExpanded =
      this.expandedLocationDetailResourceIds()[resourceIdForFiltering] ??
      this.locationDetailOrderRows().find((row) => row.resource.idForFiltering === resourceIdForFiltering)?.expanded ??
      false;

    this.expandedLocationDetailResourceIds.update((expandedResourceIds) => ({
      ...expandedResourceIds,
      [resourceIdForFiltering]: !currentlyExpanded,
    }));
  }

  setAddingOrderItem(resourceIdForFiltering: number, adding: boolean) {
    this.addingOrderItemResourceIds.update((addingResourceIds) => ({
      ...addingResourceIds,
      [resourceIdForFiltering]: adding,
    }));
  }

  onOrderServiceSelected(resourceIdForFiltering: number, item: ServiceSelectDataItem) {
    this.updateOrderDraft(resourceIdForFiltering, (draft) => ({
      ...draft,
      items: [
        ...draft.items,
        {
          clientId: `order-item-${++this.nextOrderDraftItemId}`,
          serviceId: item.id,
          name: this.getLocalizedName(item.name),
          imageUrl: item.imageUrl,
          imagePreferCover: item.imagePreferCover,
          imageObjectPosition: item.imageObjectPosition,
          noteAllowed: item.noteAllowed,
          noteMinLength: item.noteMinLength ?? 0,
          note: '',
        },
      ],
    }));
    this.setAddingOrderItem(resourceIdForFiltering, false);
  }

  removeOrderItem(resourceIdForFiltering: number, clientId: string) {
    this.updateOrderDraft(resourceIdForFiltering, (draft) => ({
      ...draft,
      items: draft.items.filter((item) => item.clientId !== clientId),
    }));
  }

  setOrderItemNote(resourceIdForFiltering: number, clientId: string, note: string) {
    this.updateOrderDraft(resourceIdForFiltering, (draft) => ({
      ...draft,
      items: draft.items.map((item) => (item.clientId === clientId ? { ...item, note } : item)),
    }));
  }

  setOrderNote(resourceIdForFiltering: number, note: string) {
    this.updateOrderDraft(resourceIdForFiltering, (draft) => ({
      ...draft,
      note,
    }));
  }

  isOrderItemNoteInvalid(item: NewReservationOrderItemDraft): boolean {
    return item.noteAllowed && item.noteMinLength > 0 && item.note.trim().length < item.noteMinLength;
  }

  removeResource(resourceIdForFiltering: number) {
    const resource = this.selectedResources().find(
      (selectedResource) => selectedResource.idForFiltering === resourceIdForFiltering
    );

    if (!resource || !this.canRemoveResource(resource)) {
      return;
    }

    this.selectedResourceIds.update((resourceIds) =>
      resourceIds.filter((resourceId) => resourceId !== resourceIdForFiltering)
    );
  }

  private updateOrderDraft(
    resourceIdForFiltering: number,
    update: (draft: NewReservationOrderDraft) => NewReservationOrderDraft
  ) {
    this.orderDrafts.update((drafts) =>
      drafts.map((draft) => (draft.resourceIdForFiltering === resourceIdForFiltering ? update(draft) : draft))
    );
  }

  private createResourceAttendeeFromResource(resource: AssistResourceBM): ResourceAttendee {
    return {
      attendee: {
        emailAddress: {
          address: resource.email,
          name: this.getResourceDisplayName(resource),
        },
        required: true,
        resource: true,
      } as CalendarAttendee,
      resource,
      orderItemsCount: 0,
      files: resource.files ?? [],
      reservationsEnabled: resource.reservationsEnabled,
      navigationEnabled: resource.navigationEnabled,
    } as ResourceAttendee;
  }

  private getUniqueSaveDisableReasons(reasons: NewReservationSaveDisableReason[]): NewReservationSaveDisableReason[] {
    const seen = new Set<string>();
    const uniqueReasons: NewReservationSaveDisableReason[] = [];

    for (const reason of reasons) {
      const key = `${reason.level}:${reason.message}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      uniqueReasons.push(reason);
    }

    return uniqueReasons;
  }

  private hasRequiredIdentifiers(mode: NewReservationDialogMode): boolean {
    if (mode === 'new') {
      return true;
    }

    return this.dialogData?.mode !== 'new' && !!this.dialogData?.eventId && !!this.dialogData?.ownerEmail;
  }

  private canEditCurrentReservation(): boolean {
    if (this.mode() === 'new') {
      return !this.readonlyOther();
    }

    const reservation = this.reservation();

    return !this.dialogData?.forceReadonly && !this.readonlyOther() && reservation?.showChangeButton === true;
  }

  private confirmDelete(
    targetEventId: string,
    scope: 'event' | 'occurrence' | 'series',
    mutationScope: CalendarRecurringMutationScope
  ) {
    const reservation = this.reservation();

    if (!reservation || !this.showDeleteActions()) {
      return;
    }

    this.dialog
      .open(ReservationDeleteComponent, {
        data: {
          reservation,
          scope,
          titleKey: translation.newReservationUi.deleteConfirmation.title,
          closeKey: translation.newReservationUi.actions.close,
          questionKeys: {
            event: translation.newReservationUi.deleteConfirmation.questionEvent,
            occurrence: translation.newReservationUi.deleteConfirmation.questionOccurrence,
            series: translation.newReservationUi.deleteConfirmation.questionSeries,
          },
          confirmButtonKeys: {
            event: translation.newReservationUi.actions.deleteEvent,
            occurrence: translation.newReservationUi.actions.deleteOccurrence,
            series: translation.newReservationUi.actions.deleteSeries,
          },
        },
      })
      .closed.pipe(take(1))
      .subscribe((result) => {
        if (result !== 1) {
          return;
        }

        this.closeWithResult({
          action: 'deleted',
          eventId: targetEventId,
          iCalUId: reservation.iCalUId,
          occurrenceId: mutationScope.scope === 'single' ? mutationScope.occurrenceId : undefined,
          scope,
        });
        this.dispatchDelete(reservation, targetEventId, mutationScope);
      });
  }

  private dispatchDelete(
    reservation: IdentifiedAssistEvent,
    targetEventId: string,
    mutationScope: CalendarRecurringMutationScope
  ) {
    this.store.dispatch(
      deleteEventById({
        eventToDeleteId: targetEventId,
        icalId: reservation.iCalUId,
        resourceAttendees: reservation.resourceAttendees,
        subject: reservation.subject,
        end: reservation.end.utcDateTime,
        start: reservation.start.utcDateTime,
        showAs: reservation.showAs.value,
        mutationScope,
      })
    );
  }

  private createReservation() {
    const payload = this.getSavePayload();

    if (!payload) {
      return;
    }

    const recurrence = this.repetitiveEventsEnabled() ? this.resolveRecurrenceForSave() : null;

    this.saving.set(true);
    this.store.dispatch(
      createEvent({
        visibility: payload.visibility,
        orders: payload.totalOrderItemsCount > 0 ? payload.orders : [],
        showAs: payload.showAs,
        onlineMeeting: payload.onlineMeeting ?? undefined,
        webexMeeting: payload.webexMeeting ?? undefined,
        subject: payload.subject,
        text: payload.bodyHtml,
        start: payload.start,
        end: payload.end,
        isAllDay: payload.isAllDay,
        resourceAttendees: payload.resourceAttendees,
        attendees: payload.nonResourceAttendees,
        reminder: payload.reminder,
        recurrence: this.resolveEffectiveRecurrenceForSave(recurrence),
        skipReservationNavigation: this.dialogData?.closeOnSave === true,
        suppressCreatedNotification: this.dialogData?.creationWorkflowNotifications === true,
      })
    );

    if (this.dialogData?.closeOnSave) {
      this.closeWithResult({
        action: 'saved',
        mode: 'new',
        createdEvent: this.createCreatedEventResult(payload),
      });
      return;
    }

    this.saving.set(false);
  }

  private createCreatedEventResult(payload: NewReservationSavePayload): NewReservationCreatedEventResult {
    return {
      subject: payload.subject,
      start: payload.start,
      end: payload.end,
      roomEmails: payload.resourceAttendees.map((resourceAttendee) => resourceAttendee.resource.email),
    };
  }

  private patchReservation() {
    const reservation = this.reservation();
    const payload = this.getSavePayload();

    if (!reservation || !payload) {
      return;
    }

    const selectedEditScope = this.selectedEditScope();
    const recurrence =
      this.canEditRecurrencePattern() && selectedEditScope === 'seriesMaster' ? this.resolveRecurrenceForSave() : null;
    const targetEventId = this.resolvePatchTargetEventId(reservation, selectedEditScope);
    const mutationScope = this.resolveMutationScopeForSingleAwareOperation(reservation, selectedEditScope);

    this.saving.set(true);
    this.calendarService
      .patchEvent(
        payload.visibility,
        payload.showAs,
        targetEventId,
        payload.subject,
        payload.bodyHtml,
        CalendarEventDatetime.fromBrowserDatetime(payload.start),
        CalendarEventDatetime.fromBrowserDatetime(payload.end),
        payload.locations,
        payload.calendarAttendees,
        payload.onlineMeeting,
        payload.reminder,
        this.resolveEffectiveRecurrenceForSave(recurrence),
        selectedEditScope,
        mutationScope,
        { isAllDay: payload.isAllDay }
      )
      .pipe(
        switchMap(() => this.notifyEventModified(reservation, payload, targetEventId)),
        delay(this.configService.getMode() === 'touchone-calendar' ? 0 : 3000),
        tap(() => {
          this.dispatchSaveRefresh(targetEventId, reservation);
          this.saving.set(false);
          this.closeWithResult({
            action: 'saved',
            mode: 'edit',
            eventId: targetEventId,
            iCalUId: reservation.iCalUId,
          });
        }),
        catchError(() => {
          this.saving.set(false);
          this.saveFailed.set(true);
          return of(null);
        })
      )
      .subscribe();
  }

  private getSavePayload(): NewReservationSavePayload | null {
    const value = this.reservationForm.getRawValue();
    const inputStart = this.normalizeDateInput(value.start);
    const inputEnd = this.normalizeDateInput(value.end);
    const isAllDay = this.supportsAllDayEvents() && value.allDay;
    const { start, end } = isAllDay
      ? this.normalizeAllDayRange(inputStart, inputEnd)
      : { start: inputStart, end: inputEnd };
    const resourceAttendees = this.selectedResourceAttendees();

    if (resourceAttendees.length === 0 || end <= start) {
      this.reservationForm.markAllAsTouched();
      return null;
    }

    const resources = resourceAttendees.map((resourceAttendee) => resourceAttendee.resource);
    const primaryResource = resources[0];
    const selectedAttendees = this.selectedAttendees();
    const orders = this.getOrderDescriptorsForSave();
    const nonResourceAttendees = this.getNonResourceAttendeesForSave(selectedAttendees, resourceAttendees);

    return {
      visibility: this.reservation()?.visibility ?? 'public',
      showAs: value.showAs,
      subject: this.getPatchedSubject(primaryResource, start, end, selectedAttendees),
      bodyHtml: this.getPatchedBody(primaryResource, start, end, selectedAttendees),
      start,
      end,
      isAllDay,
      resourceAttendees,
      locations: resourceAttendees.map((resourceAttendee) => ({
        roomName: this.getResourceDisplayName(resourceAttendee.resource),
        roomEmail: resourceAttendee.resource.email,
        roomUsername: resourceAttendee.resource.username,
      })),
      nonResourceAttendees,
      calendarAttendees: [
        ...resourceAttendees.map(
          (resourceAttendee): NewReservationAttendeeForSave => ({
            isResource: true,
            address: resourceAttendee.resource.email,
            name: this.getResourceDisplayName(resourceAttendee.resource),
            type: 'required',
          })
        ),
        ...nonResourceAttendees,
      ],
      onlineMeeting: this.getOnlineMeetingForSave(value.onlineMeetingProvider),
      webexMeeting: this.getWebexMeetingForSave(value.onlineMeetingProvider),
      reminder: this.getReminderForSave(),
      orders,
      totalOrderItemsCount: orders.reduce((total, order) => total + order.items.length, 0),
    };
  }

  private notifyEventModified(
    reservation: IdentifiedAssistEvent,
    payload: NewReservationSavePayload,
    targetEventId: string
  ) {
    const currentUser = this.currentUser()?.data;
    const organiserEmail = currentUser?.email ?? reservation.organizer?.emailAddress.address ?? '';
    const organiserName = currentUser?.name ?? reservation.organizer?.emailAddress.name ?? organiserEmail;

    return forkJoin(
      payload.resourceAttendees.map((resourceAttendee) => {
        const foundOrder = payload.orders.find(
          (order) => order.resourceIdForFiltering === resourceAttendee.resource.idForFiltering
        );

        return this.apiService.call(
          eventModified({
            data: {
              id: targetEventId,
              resourceUsername: resourceAttendee.resource.username,
              subject: payload.subject,
              showAs: payload.showAs.value,
              realStart: payload.start,
              realEnd: payload.end,
              originalRealStart: reservation.start.utcDateTime,
              originalRealEnd: reservation.end.utcDateTime,
              iCalUId: reservation.iCalUId,
              action: 'modify',
              doneBy: 'assist',
              organiserEmail,
              organiserName,
              resourceDisplayName: this.getResourceDisplayName(resourceAttendee.resource),
            },
            orders: foundOrder && foundOrder.items.length > 0 ? [foundOrder] : [],
          })
        );
      })
    );
  }

  private dispatchSaveRefresh(targetEventId: string, reservation: IdentifiedAssistEvent) {
    const rangeStart = set(new Date(), {
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });

    this.store.dispatch(refreshDashboard());
    this.store.dispatch(loadFavourites());
    this.store.dispatch(
      loadEventsPatched({
        startFrom: rangeStart,
        endFrom: add(rangeStart, { days: 30 }),
      })
    );
    this.store.dispatch(
      eventPatched({
        id: targetEventId,
        start: new Date(reservation.start.utcDateTime),
        end: new Date(reservation.end.utcDateTime),
      })
    );
  }

  private resolveRecurrenceForSave(): CalendarEventRecurrence | null {
    return this.formRecurrence();
  }

  private resolveEffectiveRecurrenceForSave(
    recurrence: CalendarEventRecurrence | null
  ): CalendarEventRecurrence | null {
    if (!recurrence) {
      return null;
    }

    return this.getRecurringSchedulingValidation(recurrence).effectiveRecurrence;
  }

  private shouldCheckRepetitiveAvailability(
    recurrence: CalendarEventRecurrence | null
  ): recurrence is CalendarEventRecurrence {
    return (
      recurrence != null &&
      this.uiState() === 'edit' &&
      this.repetitiveEventsEnabled() &&
      (this.mode() === 'new' || this.selectedEditScope() === 'seriesMaster')
    );
  }

  private getRecurringOverLimitActionId(): RecurringOverLimitActionId {
    const action = this.configService.value.recurringOverLimitActionId;
    if (action === RecurringOverLimitActionId.ScopeOut || action === RecurringOverLimitActionId.IgnoreAndWarn) {
      return action;
    }

    return RecurringOverLimitActionId.Disallow;
  }

  private getStrictestRecurringLimit(): { limit: number; resourceName: string } | null {
    let strictest: { limit: number; resourceName: string } | null = null;

    for (const resource of this.selectedResources()) {
      const limit = resource.schedulingSettings?.recurringMaxAdvanceDays;
      if (limit == null) {
        continue;
      }

      const resourceName = this.getResourceDisplayName(resource);

      if (strictest == null || limit < strictest.limit) {
        strictest = { limit, resourceName };
      }
    }

    return strictest;
  }

  private buildScopedRecurrence(
    recurrence: CalendarEventRecurrence,
    limit: number,
    isGspace: boolean,
    limitEndDateString: string
  ): CalendarEventRecurrence {
    if (isGspace) {
      return {
        ...recurrence,
        range: {
          ...recurrence.range,
          type: 'numbered',
          numberOfOccurrences: limit,
          endDate: undefined,
        },
      };
    }

    return {
      ...recurrence,
      range: {
        ...recurrence.range,
        type: 'endDate',
        endDate: limitEndDateString,
        numberOfOccurrences: undefined,
      },
    };
  }

  private getRecurringSchedulingValidation(
    recurrence: CalendarEventRecurrence
  ): NewReservationRecurringSchedulingValidationResult {
    const blockingReasons: string[] = [];
    const warningReasons: string[] = [];
    let effectiveRecurrence = recurrence;
    const { start, end } = this.availabilityRange();
    const startDatetime = CalendarEventDatetime.fromBrowserDatetime(start);
    const endDatetime = CalendarEventDatetime.fromBrowserDatetime(end);
    const providerValidationEnd = resolveValidationWindowEnd(
      start,
      this.repetitiveEventsService.getPolicy().validationWindow
    );
    const occurrences = expandRecurrenceOccurrences(
      recurrence,
      startDatetime,
      endDatetime,
      start,
      providerValidationEnd
    );
    const strictestRecurringLimit = this.getStrictestRecurringLimit();
    const isGspace = this.configService.isGspace();
    const recurringLimit = strictestRecurringLimit?.limit ?? null;
    const recurringLimitResourceName = strictestRecurringLimit?.resourceName ?? '';

    for (let i = 1; i < occurrences.length; i++) {
      const previousOccurrence = occurrences[i - 1];
      const currentOccurrence = occurrences[i];
      if (currentOccurrence.start.getTime() < previousOccurrence.end.getTime()) {
        blockingReasons.push(
          this.translocoService.translate(translation.newReservationUi.save.recurringEventOccurrencesOverlap)
        );
        break;
      }
    }

    if (recurringLimit != null) {
      const recurringAdvanceEnd = endOfDay(addDays(start, recurringLimit));
      const recurringLimitEndDateString = formatInTimeZone(recurringAdvanceEnd, 'UTC', 'yyyy-MM-dd');
      const explicitOccurrenceCount =
        recurrence.range.type === 'numbered' ? recurrence.range.numberOfOccurrences : null;
      const occurrenceCount = explicitOccurrenceCount ?? occurrences.length;
      const isNoEnd = recurrence.range.type === 'noEnd';
      const exceedsDaysLimit =
        recurrence.range.type === 'endDate' && recurrence.range.endDate
          ? endOfDay(new Date(`${recurrence.range.endDate}T00:00:00`)) > recurringAdvanceEnd
          : recurrence.range.type === 'numbered'
            ? (() => {
                const lastOccurrence = occurrences.length > 0 ? occurrences[occurrences.length - 1] : null;
                return !!lastOccurrence && lastOccurrence.end > recurringAdvanceEnd;
              })()
            : false;
      const exceedsOccurrencesLimit = occurrenceCount > recurringLimit;
      const isOverLimit = isNoEnd || (isGspace ? exceedsOccurrencesLimit : exceedsDaysLimit);

      if (isOverLimit) {
        const action = this.getRecurringOverLimitActionId();
        if (action === RecurringOverLimitActionId.ScopeOut) {
          effectiveRecurrence = this.buildScopedRecurrence(
            recurrence,
            recurringLimit,
            isGspace,
            recurringLimitEndDateString
          );
          warningReasons.push(
            isGspace
              ? this.translocoService.translate(
                  translation.newReservationUi.save.recurringEventScopeoutAppliedOccurrences,
                  {
                    occurrences: recurringLimit,
                  }
                )
              : this.translocoService.translate(translation.newReservationUi.save.recurringEventScopeoutAppliedDays, {
                  days: recurringLimit,
                })
          );
        } else if (action === RecurringOverLimitActionId.IgnoreAndWarn) {
          warningReasons.push(
            isGspace
              ? this.translocoService.translate(
                  translation.newReservationUi.save.recurringEventOverLimitIgnoredOccurrencesWarning,
                  {
                    occurrences: recurringLimit,
                  }
                )
              : this.translocoService.translate(
                  translation.newReservationUi.save.recurringEventOverLimitIgnoredDaysWarning,
                  {
                    days: recurringLimit,
                  }
                )
          );
        } else if (isNoEnd) {
          blockingReasons.push(
            isGspace
              ? this.translocoService.translate(
                  translation.newReservationUi.save.recurringEventRequiresEndWithinOccurrences,
                  {
                    resourceName: recurringLimitResourceName,
                    occurrences: recurringLimit,
                  }
                )
              : this.translocoService.translate(
                  translation.newReservationUi.save.recurringEventRequiresEndDateWithinDays,
                  {
                    resourceName: recurringLimitResourceName,
                    days: recurringLimit,
                  }
                )
          );
        } else {
          blockingReasons.push(
            isGspace
              ? this.translocoService.translate(translation.newReservationUi.save.recurringEventExceedsMaxOccurrences, {
                  resourceName: recurringLimitResourceName,
                  occurrences: recurringLimit,
                })
              : this.translocoService.translate(translation.newReservationUi.save.recurringEventExceedsBookingWindow, {
                  resourceName: recurringLimitResourceName,
                  days: recurringLimit,
                })
          );
        }
      }
    }

    const recurrenceForRules = effectiveRecurrence;
    const occurrencesForRules =
      recurrenceForRules === recurrence
        ? occurrences
        : expandRecurrenceOccurrences(recurrenceForRules, startDatetime, endDatetime, start, providerValidationEnd);

    for (const resource of this.selectedResources()) {
      const settings = resource.schedulingSettings;
      if (!settings) {
        continue;
      }

      const resourceName = this.getResourceDisplayName(resource);

      if (settings.allowRecurringMeetings === false) {
        blockingReasons.push(
          this.translocoService.translate(translation.newReservationUi.save.recurringMeetingsNotAllowedForResource, {
            resourceName,
          })
        );
        continue;
      }

      const bookingWindowInDays = settings.bookingWindowInDays;
      const enforceSchedulingHorizon = settings.enforceSchedulingHorizon ?? true;
      if (bookingWindowInDays == null || !enforceSchedulingHorizon) {
        continue;
      }

      const horizonEnd = endOfDay(addDays(start, bookingWindowInDays));

      if (recurrenceForRules.range.type === 'noEnd') {
        blockingReasons.push(
          this.translocoService.translate(translation.newReservationUi.save.recurringEventRequiresEndDateWithinDays, {
            resourceName,
            days: bookingWindowInDays,
          })
        );
        continue;
      }

      if (recurrenceForRules.range.type === 'endDate' && recurrenceForRules.range.endDate) {
        const recurrenceEndDate = endOfDay(new Date(`${recurrenceForRules.range.endDate}T00:00:00`));
        if (recurrenceEndDate > horizonEnd) {
          blockingReasons.push(
            this.translocoService.translate(translation.newReservationUi.save.recurringEventExceedsBookingWindow, {
              resourceName,
              days: bookingWindowInDays,
            })
          );
        }
        continue;
      }

      if (recurrenceForRules.range.type === 'numbered' && recurrenceForRules.range.numberOfOccurrences != null) {
        const lastOccurrence =
          occurrencesForRules.length > 0 ? occurrencesForRules[occurrencesForRules.length - 1] : null;
        if (lastOccurrence && lastOccurrence.end > horizonEnd) {
          blockingReasons.push(
            this.translocoService.translate(translation.newReservationUi.save.recurringEventExceedsBookingWindow, {
              resourceName,
              days: bookingWindowInDays,
            })
          );
        }
      }
    }

    return {
      effectiveRecurrence,
      blockingReasons,
      warningReasons,
    };
  }

  private getRepetitiveAvailabilityDisableReasons(
    result: RepetitiveAvailabilityResult
  ): NewReservationSaveDisableReason[] {
    if (result.conflictedOccurrences === 0) {
      return [];
    }

    const allowedBySchedulingRules = this.isRecurringConflictAllowedBySchedulingRules(
      result.conflictedOccurrences,
      result.totalOccurrences
    );

    return [
      {
        level: allowedBySchedulingRules ? 'warning' : 'error',
        message: this.translocoService.translate(translation.newReservationUi.save.recurringConflictsDetected, {
          conflicted: result.conflictedOccurrences,
          total: result.totalOccurrences,
        }),
      },
    ];
  }

  private isRecurringConflictAllowedBySchedulingRules(
    conflictedOccurrences: number,
    totalOccurrences: number
  ): boolean {
    if (conflictedOccurrences === 0) {
      return true;
    }

    const conflictPercentage = totalOccurrences === 0 ? 0 : (conflictedOccurrences / totalOccurrences) * 100;

    for (const resource of this.selectedResources()) {
      const settings = resource.schedulingSettings;
      if (!settings) {
        return false;
      }

      const areConflictsAllowed = settings.allowConflicts ?? settings.allowRecurringConflicts;
      if (areConflictsAllowed !== true) {
        return false;
      }

      if (settings.maximumConflictInstances != null && conflictedOccurrences > settings.maximumConflictInstances) {
        return false;
      }

      if (settings.conflictPercentageAllowed != null && conflictPercentage > settings.conflictPercentageAllowed) {
        return false;
      }
    }

    return true;
  }

  private resolvePatchTargetEventId(
    reservation: IdentifiedAssistEvent,
    selectedEditScope: CalendarEventEditScope
  ): string {
    return resolveRecurringMutationTargetEventId(reservation, selectedEditScope, (event) =>
      this.calendarService.getSingleOccurrenceMutationTargetEventId(event)
    );
  }

  private resolveMutationScopeForSingleAwareOperation(
    reservation: IdentifiedAssistEvent,
    selectedEditScope: CalendarEventEditScope
  ): CalendarRecurringMutationScope {
    return resolveRecurringMutationScope(reservation, selectedEditScope);
  }

  private getOrderDescriptorsForSave(): OrderDescriptor[] {
    return this.orderDrafts()
      .map(
        (draft): OrderDescriptor => ({
          note: draft.note,
          resourceIdForFiltering: draft.resourceIdForFiltering,
          items: draft.items.map(
            (item): OrderItemDescriptor => ({
              note: item.note,
              serviceId: item.serviceId,
              cancelled: false,
            })
          ),
        })
      )
      .filter((order) => order.items.length > 0);
  }

  private getNonResourceAttendeesForSave(
    attendees: NewReservationAttendeeModel[],
    resourceAttendees: ResourceAttendee[]
  ): NewReservationAttendeeForSave[] {
    const resourceEmails = new Set(
      resourceAttendees.map((resourceAttendee) => this.normalizeEmail(resourceAttendee.resource.email))
    );

    return attendees
      .filter((attendee) => !resourceEmails.has(this.normalizeEmail(attendee.email)))
      .map((attendee) => ({
        isResource: false,
        address: attendee.email,
        name: attendee.name,
        type: attendee.participation,
      }));
  }

  private getOnlineMeetingForSave(onlineMeetingProvider: string | null): NewReservationOnlineMeetingForSave {
    if (onlineMeetingProvider === 'webex') {
      return null;
    }

    return onlineMeetingProvider ? { onlineMeetingProvider } : null;
  }

  private getWebexMeetingForSave(onlineMeetingProvider: string | null): WebexUserSettingsModel | null {
    return onlineMeetingProvider === 'webex' ? this.pairedWebexSettings() : null;
  }

  private getReminderForSave(): CalendarEventReminder {
    return (
      this.reservation()?.reminder ?? {
        enabled: false,
        minutesBeforeStart: 0,
      }
    );
  }

  private getPatchedSubject(
    resource: AssistResourceBM,
    start: Date,
    end: Date,
    attendees: NewReservationAttendeeModel[]
  ): string {
    return replaceReservationPlaceholders(
      this.reservationForm.getRawValue().subject,
      this.getReservationPlaceholderValues(resource, start, end, attendees, this.getResourceInfoUrl(resource))
    );
  }

  private getPatchedBody(
    resource: AssistResourceBM,
    start: Date,
    end: Date,
    attendees: NewReservationAttendeeModel[]
  ): string {
    return replaceReservationPlaceholders(
      this.reservationForm.getRawValue().bodyHtml,
      this.getReservationPlaceholderValues(resource, start, end, attendees, this.getResourceInfoLink(resource))
    );
  }

  private getReservationPlaceholderValues(
    resource: AssistResourceBM,
    start: Date,
    end: Date,
    attendees: NewReservationAttendeeModel[],
    resourceUrl: string
  ): ReservationPlaceholderValues {
    return {
      'resource-url': resourceUrl,
      'start-date': this.formatDatePlaceholder(start),
      'end-date': this.formatDatePlaceholder(end),
      'start-time': this.formatTimePlaceholder(start),
      'end-time': this.formatTimePlaceholder(end),
      'resource-capacity':
        resource.capacity != null
          ? resource.capacity.toString()
          : this.translocoService.translate(translation.common.unlimited),
      'attendees-count': attendees.length,
      'resource-display-name': this.getResourceDisplayName(resource),
      'resource-type': this.getResourceTypeName(resource),
    };
  }

  private formatDatePlaceholder(value: Date): string {
    return String(this.dateFormatPipe.transform(value) ?? '');
  }

  private formatTimePlaceholder(value: Date): string {
    return String(this.timeFormatPipe.transform(value) ?? '');
  }

  private getResourceInfoUrl(resource: AssistResourceBM): string {
    return `${location.protocol}//${location.host}/dashboard?multiviewResource=${encodeURIComponent(resource.id)}`;
  }

  private getResourceInfoLink(resource: AssistResourceBM): string {
    return `<a target="_blank" href="${this.getResourceInfoUrl(resource)}">${this.getResourceDisplayName(resource)}</a>`;
  }

  private closeWithResult(result: NewReservationDialogResult) {
    if (this.modal) {
      this.modal.close(result);
      return;
    }

    this.close.emit(result);
  }

  private getDefaultShowAs(): NewReservationShowAsOption {
    return (
      this.showAsOptions().find(
        (showAs) => showAs.value.toUpperCase() === 'FREE' || showAs.value.toUpperCase() === 'TRANSPARENT'
      ) ??
      this.showAsOptions()[0] ?? {
        key: translation.newReservationUi.administration.showAsFree,
        text: this.translocoService.translate(translation.newReservationUi.administration.showAsFree),
        value: 'free',
      }
    );
  }

  private resolveShowAsOption(showAs: { value?: string | null } | null | undefined): NewReservationShowAsOption | null {
    if (!showAs?.value) {
      return null;
    }

    return this.showAsOptions().find((option) => option.value.toUpperCase() === showAs.value.toUpperCase()) ?? null;
  }

  private getShowAsLabelKey(value: string): string {
    switch (value.toUpperCase()) {
      case 'FREE':
      case 'TRANSPARENT':
        return translation.newReservationUi.administration.showAsFree;
      case 'TENTATIVE':
        return translation.newReservationUi.administration.showAsTentative;
      case 'BUSY':
      case 'OPAQUE':
        return translation.newReservationUi.administration.showAsBusy;
      case 'WORKINGELSEWHERE':
        return translation.newReservationUi.administration.showAsWorkingElsewhere;
      case 'OOF':
        return translation.newReservationUi.administration.showAsAway;
      default:
        return translation.newReservationUi.administration.showAsBusy;
    }
  }

  private getDefaultStart(): Date {
    const start = new Date();
    start.setSeconds(0, 0);

    return start;
  }

  private getDefaultEnd(start: Date): Date {
    return new Date(start.getTime() + 60 * 60 * 1000);
  }

  private getDefaultOnlineMeetingProvider(): NewReservationOnlineMeetingProvider | null {
    if (this.configService.isO365() && this.configService.value.autoMeetingSettings_Teams) {
      return 'teamsForBusiness';
    }

    if (this.configService.isGspace() && this.configService.value.autoMeetingSettings_Meet) {
      return 'hangoutsMeet';
    }

    if (this.configService.value.autoMeetingSettings_Webex && this.pairedWebexSettings()) {
      return 'webex';
    }

    return null;
  }

  private resolveExistingOnlineMeetingProvider(
    reservation: IdentifiedAssistEvent
  ): NewReservationOnlineMeetingProvider | null {
    if (!reservation.onlineMeetingProvider) {
      return null;
    }

    if (this.configService.isO365()) {
      return 'teamsForBusiness';
    }

    if (this.configService.isGspace()) {
      return 'hangoutsMeet';
    }

    return null;
  }

  private createAttendeeModelsFromReservation(reservation: IdentifiedAssistEvent): NewReservationAttendeeModel[] {
    const organizer = reservation.organizer?.emailAddress;
    const attendees = organizer?.address
      ? [
          {
            email: organizer.address,
            name: organizer.name || organizer.address,
            participation: 'required' as const,
            isOrganizer: true,
            responseStatus: 'none' as const,
          },
        ]
      : [];

    return this.mergeAttendees([
      ...attendees,
      ...reservation.attendees
        .filter((attendee) => !attendee.resource)
        .map((attendee) => this.createAttendeeModelFromCalendarAttendee(attendee)),
    ]);
  }

  private createAttendeeModelFromCalendarAttendee(attendee: CalendarAttendee): NewReservationAttendeeModel {
    return {
      email: attendee.emailAddress.address,
      name: attendee.emailAddress.name || attendee.emailAddress.address,
      participation: attendee.required ? 'required' : 'optional',
      isOrganizer: false,
      responseStatus: this.getAttendeeResponseStatus(attendee.status?.response),
    };
  }

  private createAttendeeModelsFromEmails(
    requiredAttendeeEmails: string[],
    optionalAttendeeEmails: string[]
  ): NewReservationAttendeeModel[] {
    return this.mergeAttendees([
      ...requiredAttendeeEmails.map((email) => ({
        email,
        name: email,
        participation: 'required' as const,
        isOrganizer: false,
        responseStatus: 'none' as const,
      })),
      ...optionalAttendeeEmails.map((email) => ({
        email,
        name: email,
        participation: 'optional' as const,
        isOrganizer: false,
        responseStatus: 'none' as const,
      })),
    ]);
  }

  private mergeAttendees(attendees: NewReservationAttendeeModel[]): NewReservationAttendeeModel[] {
    const attendeeByEmail = new Map<string, NewReservationAttendeeModel>();

    for (const attendee of attendees) {
      const email = this.normalizeEmail(attendee.email);

      if (!email) {
        continue;
      }

      const existing = attendeeByEmail.get(email);

      attendeeByEmail.set(email, {
        ...attendee,
        isOrganizer: attendee.isOrganizer || existing?.isOrganizer === true,
        participation: existing?.participation === 'required' ? 'required' : attendee.participation,
        responseStatus: this.mergeAttendeeResponseStatus(existing?.responseStatus, attendee.responseStatus),
      });
    }

    return [...attendeeByEmail.values()];
  }

  private getAttendeeInitials(attendee: NewReservationAttendeeModel): string {
    const nameParts = attendee.name.trim().split(/\s+/).filter(Boolean);

    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }

    return (nameParts[0]?.[0] ?? attendee.email[0] ?? '?').toUpperCase();
  }

  private getAttendeeAvailabilityState(
    attendee: NewReservationAttendeeModel
  ): NewReservationResourceAvailabilityDisplay {
    return (
      this.attendeeAvailabilityByEmail().get(this.normalizeEmail(attendee.email)) ??
      (this.attendeeAvailabilityParams()
        ? this.getLoadingAvailabilityDisplay()
        : this.getUnknownAttendeeAvailabilityDisplay())
    );
  }

  private resolveAttendeeAvailability(email: string, start: Date, end: Date) {
    return this.dataService.getScheduleForAttendee(email, start, end).pipe(
      map((availabilityView) => {
        const availability = availabilityView.split('') as CalendarAvailability[];
        const reservation = this.reservation();
        const patchedAvailability =
          reservation && this.isAttendeeOnLoadedReservation(email)
            ? getPatchedAvailabilityWithEvent(
                availability,
                start,
                new Date(reservation.start.utcDateTime),
                new Date(reservation.end.utcDateTime)
              )
            : availability;

        return this.getAttendeeAvailabilityDisplay(getTotallAvailability(patchedAvailability));
      }),
      catchError(() =>
        of({
          availabilityState: 'warning',
          availabilityLabelKey: translation.newReservationUi.administration.availabilityWarning,
          availabilityReasons: [
            this.translocoService.translate(translation.newReservationUi.administration.availabilityCouldNotBeChecked),
          ],
        } satisfies NewReservationResourceAvailabilityDisplay)
      )
    );
  }

  private getAttendeeAvailabilityDisplay(
    availability: CalendarAvailability
  ): NewReservationResourceAvailabilityDisplay {
    if (availability === CalendarAvailability.free || availability === CalendarAvailability.freeOutsideWorkingHours) {
      return {
        availabilityState: 'available',
        availabilityLabelKey: translation.newReservationUi.administration.availabilityAvailable,
        availabilityReasons: [],
      };
    }

    if (availability === CalendarAvailability.tentative) {
      return {
        availabilityState: 'warning',
        availabilityLabelKey: translation.newReservationUi.administration.availabilityWarning,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.administration.availabilityTentative),
        ],
      };
    }

    return {
      availabilityState: 'error',
      availabilityLabelKey: translation.newReservationUi.administration.availabilityBusy,
      availabilityReasons: [
        this.translocoService.translate(translation.newReservationUi.administration.availabilityBusyReason),
      ],
    };
  }

  private getUnknownAttendeeAvailabilityDisplay(): NewReservationResourceAvailabilityDisplay {
    return {
      availabilityState: 'unknown',
      availabilityLabelKey: translation.newReservationUi.administration.availabilityUnknown,
      availabilityReasons: [],
    };
  }

  private isAttendeeOnLoadedReservation(email: string): boolean {
    const reservation = this.reservation();
    const normalizedEmail = this.normalizeEmail(email);

    if (!reservation || !normalizedEmail) {
      return false;
    }

    const organizerEmail = this.normalizeEmail(reservation.organizer?.emailAddress?.address ?? '');

    return (
      organizerEmail === normalizedEmail ||
      reservation.attendees.some(
        (attendee) =>
          !attendee.resource && this.normalizeEmail(attendee.emailAddress?.address ?? '') === normalizedEmail
      )
    );
  }

  getParticipationLabel(participation: NewReservationAttendeeParticipation): string {
    return this.participationOptions().find((option) => option.value === participation)?.text ?? participation;
  }

  getAttendeeResponseStatusLabel(status: NewReservationAttendeeResponseStatus): string {
    switch (status) {
      case 'accepted':
        return this.translocoService.translate(translation.newReservationUi.administration.responseAccepted);
      case 'rejected':
        return this.translocoService.translate(translation.newReservationUi.administration.responseRejected);
      case 'tentative':
        return this.translocoService.translate(translation.newReservationUi.administration.responseTentative);
      case 'none':
      default:
        return this.translocoService.translate(translation.newReservationUi.administration.responseNone);
    }
  }

  private getAttendeeResponseStatus(
    response: CalendarAttendee['status']['response'] | undefined
  ): NewReservationAttendeeResponseStatus {
    switch (response) {
      case 'accepted':
      case 'Accept':
        return 'accepted';
      case 'declined':
      case 'Decline':
        return 'rejected';
      case 'Tentative':
      case 'tentative':
      case 'tentativelyAccepted':
        return 'tentative';
      case 'NoResponseReceived':
      case 'None':
      case 'none':
      case 'notResponded':
      case 'organizer':
      default:
        return 'none';
    }
  }

  private mergeAttendeeResponseStatus(
    existing: NewReservationAttendeeResponseStatus | undefined,
    next: NewReservationAttendeeResponseStatus
  ): NewReservationAttendeeResponseStatus {
    return existing && existing !== 'none' && next === 'none' ? existing : next;
  }

  private normalizeEmail(email: string): string {
    return email.trim().toLocaleLowerCase();
  }

  private getLocalizedName(value: Parameters<typeof fromLocalized>[0]): string {
    return fromLocalized(value, this.translocoService.getActiveLang(), this.translocoService.getDefaultLang()) ?? '';
  }

  private getResourceDisplayName(resource: AssistResourceBM): string {
    return (
      this.getLocalizedName(resource.displayNameForApp) || this.getLocalizedName(resource.displayName) || resource.name
    );
  }

  private getResourceTypeName(resource: AssistResourceBM): string {
    const resourceType = this.configService.getResourceTypeFromId(resource.resourceTypeId);

    return (resourceType ? this.getLocalizedName(resourceType.name) : '') || resource.resourceTypeName;
  }

  private getSelectedResourceAvailabilityState(resource: AssistResourceBM): NewReservationResourceAvailabilityDisplay {
    if (this.resourceTypeRestrictionCheckFailed()) {
      return this.getFailedAvailabilityCheckDisplay();
    }

    if (this.restrictedSelectedResourceIds().has(resource.idForFiltering)) {
      return this.getMultipleResourceTypeRestrictionAvailabilityDisplay();
    }

    const eventStatusAvailability = this.getEventStatusAvailabilityState(resource);

    if (eventStatusAvailability) {
      return eventStatusAvailability;
    }

    return (
      this.selectedResourceAvailabilityById().get(resource.idForFiltering) ??
      (this.selectedResourceAvailabilityParams()
        ? this.getLoadingAvailabilityDisplay()
        : this.getUnknownAvailabilityDisplay())
    );
  }

  private getEventStatusAvailabilityState(
    resource: AssistResourceBM
  ): NewReservationResourceAvailabilityDisplay | null {
    if (!resource.reservationsEnabled) {
      return {
        availabilityState: 'error',
        availabilityLabelKey: translation.newReservationUi.location.availabilityError,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.location.reservationsDisabled),
        ],
      };
    }

    const resourceAttendee = this.reservation()?.resourceAttendees.find(
      (attendee) => attendee.resource.idForFiltering === resource.idForFiltering
    );

    if (!resourceAttendee) {
      return null;
    }

    if (
      resourceAttendee.status === 'rejected' ||
      resourceAttendee.status === 'release-failed' ||
      resourceAttendee.status === 'released'
    ) {
      return {
        availabilityState: 'error',
        availabilityLabelKey: translation.newReservationUi.location.availabilityError,
        availabilityReasons: [this.translocoService.translate(translation.newReservationUi.location.resourceDeclined)],
      };
    }

    if (
      resourceAttendee.status === 'tentative' ||
      resourceAttendee.status === 'need-confirmation' ||
      resourceAttendee.status === 'confirm-now' ||
      resourceAttendee.status === 'unconfirmed'
    ) {
      return {
        availabilityState: 'warning',
        availabilityLabelKey: translation.newReservationUi.location.availabilityWarning,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.location.resourceNeedsConfirmation),
        ],
      };
    }

    return null;
  }

  private loadResourceAvailabilities(
    resources: AssistResourceBM[],
    start: Date,
    end: Date,
    patchExistingReservation: boolean
  ) {
    const reservableResources = resources.filter((resource) => resource.reservationsEnabled);
    const reservation = patchExistingReservation ? this.reservation() : null;
    const existingEvent = reservation
      ? {
          start: new Date(reservation.start.utcDateTime),
          end: new Date(reservation.end.utcDateTime),
        }
      : null;
    const existingResourceIds = new Set(
      reservation?.resourceAttendees.map((resourceAttendee) => resourceAttendee.resource.idForFiltering) ?? []
    );
    const existingEventsByResourceId = new Map(
      existingEvent ? [...existingResourceIds].map((resourceId) => [resourceId, existingEvent] as const) : []
    );

    return this.dataService
      .getTimelinePatchedSchedulesForResources(reservableResources, start, end, existingEventsByResourceId)
      .pipe(
        map(
          (availabilityByResourceId) =>
            new Map<number, NewReservationResourceAvailabilityDisplay>(
              resources.map((resource) => [
                resource.idForFiltering,
                this.getResourceAvailabilityDisplay(resource, availabilityByResourceId.get(resource.idForFiltering)),
              ])
            )
        ),
        catchError(() =>
          of(
            new Map<number, NewReservationResourceAvailabilityDisplay>(
              resources.map((resource) => [
                resource.idForFiltering,
                this.getResourceAvailabilityDisplay(resource, null),
              ])
            )
          )
        )
      );
  }

  private getResourceAvailabilityDisplay(
    resource: AssistResourceBM,
    availabilityView: CalendarAvailability[] | null | undefined
  ): NewReservationResourceAvailabilityDisplay {
    if (!resource.reservationsEnabled) {
      return {
        availabilityState: 'error',
        availabilityLabelKey: translation.newReservationUi.location.availabilityError,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.location.reservationsDisabled),
        ],
      };
    }

    if (availabilityView == null) {
      return {
        availabilityState: 'warning',
        availabilityLabelKey: translation.newReservationUi.location.availabilityWarning,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.location.availabilityCouldNotBeChecked),
        ],
      };
    }

    return this.getResourcePickerAvailabilityDisplay(getTotallAvailability(availabilityView));
  }

  private getResourcePickerAvailabilityDisplay(
    availability: CalendarAvailability
  ): NewReservationResourceAvailabilityDisplay {
    if (availability === CalendarAvailability.free || availability === CalendarAvailability.freeOutsideWorkingHours) {
      return {
        availabilityState: 'available',
        availabilityLabelKey: translation.newReservationUi.location.availabilityAvailable,
        availabilityReasons: [],
      };
    }

    if (availability === CalendarAvailability.tentative) {
      return {
        availabilityState: 'warning',
        availabilityLabelKey: translation.newReservationUi.location.availabilityWarning,
        availabilityReasons: [
          this.translocoService.translate(translation.newReservationUi.location.availabilityTentative),
        ],
      };
    }

    return {
      availabilityState: 'error',
      availabilityLabelKey: translation.newReservationUi.location.availabilityError,
      availabilityReasons: [this.translocoService.translate(translation.newReservationUi.location.availabilityBlocked)],
    };
  }

  private getLoadingAvailabilityDisplay(): NewReservationResourceAvailabilityDisplay {
    return {
      availabilityState: 'loading',
      availabilityLabelKey: translation.newReservationUi.location.resourcePickerLoading,
      availabilityReasons: [],
    };
  }

  private getUnknownAvailabilityDisplay(): NewReservationResourceAvailabilityDisplay {
    return {
      availabilityState: 'unknown',
      availabilityLabelKey: translation.newReservationUi.location.availabilityUnknown,
      availabilityReasons: [],
    };
  }

  private getFailedAvailabilityCheckDisplay(): NewReservationResourceAvailabilityDisplay {
    return {
      availabilityState: 'error',
      availabilityLabelKey: translation.newReservationUi.location.availabilityError,
      availabilityReasons: [
        this.translocoService.translate(translation.newReservationUi.location.availabilityCouldNotBeChecked),
      ],
    };
  }

  private getMultipleResourceTypeRestrictionAvailabilityDisplay(): NewReservationResourceAvailabilityDisplay {
    return {
      availabilityState: 'error',
      availabilityLabelKey: translation.newReservationUi.location.availabilityError,
      availabilityReasons: [
        this.translocoService.translate(
          translation.newReservationUi.location.multipleResourceTypeReservationsNotAllowed
        ),
      ],
    };
  }

  private getResourcePickerDetail(resource: AssistResourceBM): string {
    return [
      this.getResourceTypeName(resource),
      resource.capacity != null
        ? this.translocoService.translate(translation.newReservationUi.location.resourceCapacity, {
            count: resource.capacity,
          })
        : null,
      resource.email,
    ]
      .filter((value): value is string => !!value)
      .join(' · ');
  }

  private isResourceVisibleForReservation(resource: AssistResourceBM): boolean {
    return (
      this.configService.isRestrictedResourceVisible(resource) &&
      this.licensingService.availableForServerType(resource, this.configService.getMode())
    );
  }

  private getResourceSearchText(resource: AssistResourceBM): string {
    const values = [
      resource.name,
      resource.email,
      resource.username,
      resource.id,
      `${resource.idForFiltering}`,
      `${resource.capacity ?? ''}`,
      resource.csCode,
      this.getResourceDisplayName(resource),
      this.getResourceTypeName(resource),
      this.getLocalizedText(resource.description),
      ...resource.properties.flatMap((property) => [
        property.propertyId,
        this.getLocalizedText(property.name),
        this.getLocalizedText(property.valueText),
        property.valueNumber?.toString() ?? '',
        property.valueDecimal?.toString() ?? '',
        property.valueBoolean?.toString() ?? '',
        ...(property.valueChoiceIds ?? []),
      ]),
    ];

    return this.normalizeSearchText(values.filter(Boolean).join(' '));
  }

  private getLocalizedText(value: AssistResourceBM['displayName'] | undefined | null): string {
    return value
      ? (fromLocalized(value, this.translocoService.getActiveLang(), this.translocoService.getDefaultLang()) ?? '')
      : '';
  }

  private normalizeSearchText(value: string): string {
    return remove(value).toLocaleLowerCase().trim();
  }

  private getSchedulingRulesForDisplay(resource: AssistResourceBM): string[] {
    return formatSchedulingRulesForDisplay(
      resource.schedulingSettings,
      (key, params) => this.translocoService.translate(key, params),
      {
        recurringMaxAdvanceAsOccurrences: this.configService.isGspace(),
      }
    );
  }

  private canRemoveResource(resource: AssistResourceBM): boolean {
    if (this.uiState() !== 'edit' || !this.selectedResourceIds().includes(resource.idForFiltering)) {
      return false;
    }

    if (this.mode() === 'new') {
      return true;
    }

    if (this.configService.value.enableOrders && this.previousOrders.isLoading()) {
      return false;
    }

    return !this.hasPreviousOrdersForResource(resource.idForFiltering);
  }

  private hasPreviousOrdersForResource(resourceIdForFiltering: number): boolean {
    return (this.previousOrdersByResourceId().get(resourceIdForFiltering)?.length ?? 0) > 0;
  }

  private getStableLocationDetailResourceAttendees(resourceAttendee: ResourceAttendee): ResourceAttendee[] {
    const resourceIdForFiltering = resourceAttendee.resource.idForFiltering;
    const cached = this.locationDetailResourceAttendeesCache.get(resourceIdForFiltering);

    if (cached?.resourceAttendee === resourceAttendee) {
      return cached.resourceAttendees;
    }

    const resourceAttendees = [resourceAttendee];
    this.locationDetailResourceAttendeesCache.set(resourceIdForFiltering, { resourceAttendee, resourceAttendees });

    return resourceAttendees;
  }

  private toDateOnlyString(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private normalizeDateInput(value: Date): Date {
    const date = new Date(value);
    date.setSeconds(0, 0);

    return date;
  }

  private normalizeAllDayRange(start: Date, end: Date): { start: Date; end: Date } {
    const normalizedStart = startOfDay(start);
    const normalizedEnd = startOfDay(end);

    return {
      start: normalizedStart,
      end: end.getTime() === normalizedEnd.getTime() ? normalizedEnd : addDays(normalizedEnd, 1),
    };
  }
}
